import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAppConfig } from '../context/AppConfigContext';
import { getTranslation } from '../i18n/translations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatDateTimeDMY, formatDateDMY as formatDateDMYUtil } from '../utils/dateUtils';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Trash,
  PencilSimple,
  FilePdf,
  Eye,
  CaretDown,
  CaretUp,
  ArrowLeft,
  Paperclip,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  UploadSimple,
  Printer,
  Translate,
  ArrowUp,
  ArrowDown,
  ListBullets,
  EnvelopeSimple,
  Star,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { printPdf as printPdfUtil, openPdfInBrowser as openPdfInBrowserUtil } from '../utils/pdfUtils';
import DatePickerDMY from '../components/DatePickerDMY';
import SendEmailDialog from '../components/SendEmailDialog';
import MeetingTemplatesDialog from '../components/MeetingTemplatesDialog';
import ApprovalEmailDialog from '../components/ApprovalEmailDialog';
import MeetingTranslateDialog from '../components/MeetingTranslateDialog';
import { MeetingOwnerSync } from '../components/MeetingOwnerSync';
import { buildAgendaVars, resolveAgendaVarsInText, listUnresolvedAgendaVars } from '../utils/agendaVars';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

const MEETING_TYPES = ['agm', 'extraordinary', 'committee'];

const COMMITTEE_ROLES = ['president', 'secretary', 'treasurer'];

// Phase 207 / 208 — Workflow status helper. Maps each meeting status to a label & colors.
// `legacy` (finalized) is kept for backward compatibility with old records.
// `pending_signature` is kept for backward compatibility with already-approved
// meetings created before the rename to `approved`.
const STATUS_META = {
  draft:              { label: 'ΠΡΟΧΕΙΡΟ',          cls: 'bg-zinc-100 text-zinc-600 border-zinc-300' },
  pending_approval:   { label: 'ΠΡΟΣ ΕΓΚΡΙΣΗ',      cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  needs_changes:      { label: 'ΧΡΕΙΑΖΕΤΑΙ ΑΛΛΑΓΕΣ', cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  rejected:           { label: 'ΑΠΟΡΡΙΦΘΗΚΕ',       cls: 'bg-red-50 text-red-700 border-red-300' },
  approved:           { label: 'ΕΓΚΕΚΡΙΜΕΝΟ',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  pending_signature:  { label: 'ΕΓΚΕΚΡΙΜΕΝΟ',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  completed:          { label: 'ΟΛΟΚΛΗΡΩΜΕΝΟ',      cls: 'bg-emerald-100 text-emerald-800 border-emerald-400' },
  sent:               { label: 'ΑΠΕΣΤΑΛΜΕΝΟ',       cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  finalized:          { label: 'ΟΛΟΚΛΗΡΩΜΕΝΟ',      cls: 'bg-emerald-100 text-emerald-800 border-emerald-400' },
};
const StatusBadge = ({ status, onSubmitDraft, busy }) => {
  // Phase 207k — When status === 'draft' AND a submit callback is provided,
  // render an inline action button that REPLACES the badge. One-click flow:
  // press → status flips to PENDING_APPROVAL + emails go out automatically.
  if (status === 'draft' && onSubmitDraft) {
    return (
      <button
        type="button"
        onClick={onSubmitDraft}
        disabled={busy}
        className="text-[10px] px-2 py-0.5 border border-amber-400 bg-amber-500 hover:bg-amber-600 text-white font-semibold uppercase tracking-wider transition-colors disabled:opacity-60"
        data-testid="submit-for-approval-btn"
        title="Αποστολή για έγκριση στην επιτροπή"
      >
        Αποστολή για έγκριση
      </button>
    );
  }
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`text-[10px] px-2 py-0.5 border font-semibold uppercase tracking-wider ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

// Phase 207d/207f — Editable auto-generated text with reset capability.
//   - Shows the auto/template text as a preview by default
//   - "Επεξεργασία" enters edit mode (textarea)
//   - "Επαναφορά στο αυτόματο" clears the override → falls back to auto
//   - Badge: "Αυτόματο" (when value empty), "Επεξεργασμένο" (when overridden saved),
//     or "Μη Αποθηκευμένο" (when changed but parent form not saved yet).
//
//   Single source of truth: any change immediately propagates to the parent form
//   state; the inline button just exits edit mode (no separate save). The user
//   then commits via the main "Save Meeting" button at the bottom of the page.
function EditableAutoText({ label, autoText, value, onChange, testIdBase, savedValue }) {
  const [editing, setEditing] = useState(false);
  const isOverridden = !!(value && value.trim());
  const display = isOverridden ? value : autoText;
  // Compare against the value last persisted to the meeting (passed from parent).
  // If they differ, mark the field as having unsaved changes.
  const hasUnsaved = (value || '').trim() !== (savedValue || '').trim();

  const startEdit = () => setEditing(true);
  const finishEdit = () => {
    // If the edited content matches the auto text, treat as "not overridden"
    if ((value || '').trim() === (autoText || '').trim()) {
      onChange('');
    }
    setEditing(false);
  };
  const resetToAuto = () => {
    onChange('');
    setEditing(false);
  };

  let badgeLabel = 'Αυτόματο';
  let badgeCls = 'bg-zinc-50 text-zinc-500 border-zinc-200';
  if (hasUnsaved) {
    badgeLabel = 'Μη Αποθηκευμένο';
    badgeCls = 'bg-amber-50 text-amber-700 border-amber-300';
  } else if (isOverridden) {
    badgeLabel = 'Επεξεργασμένο';
    badgeCls = 'bg-blue-50 text-blue-700 border-blue-200';
  }

  return (
    <div data-testid={`${testIdBase}-block`}>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
        <Label className="text-xs font-semibold text-zinc-700">{label}</Label>
        <div className="flex items-center gap-1.5">
          <span
            className={`text-[10px] px-1.5 py-0.5 border uppercase font-semibold ${badgeCls}`}
            data-testid={`${testIdBase}-badge`}
          >
            {badgeLabel}
          </span>
          {!editing && (
            <button
              type="button"
              onClick={startEdit}
              className="text-[10px] px-1.5 py-0.5 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 uppercase font-semibold"
              data-testid={`${testIdBase}-edit-btn`}
            >
              Επεξεργασία
            </button>
          )}
          {!editing && isOverridden && (
            <button
              type="button"
              onClick={resetToAuto}
              className="text-[10px] px-1.5 py-0.5 border border-zinc-300 text-zinc-600 hover:bg-zinc-50 uppercase font-semibold"
              title="Επαναφορά στο αυτόματο κείμενο"
              data-testid={`${testIdBase}-reset-btn`}
            >
              Επαναφορά
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            rows={5}
            className="rounded-none text-sm"
            autoFocus
            data-testid={`${testIdBase}-textarea`}
          />
          <div className="flex justify-between items-center gap-2">
            <p className="text-[10px] text-amber-700 italic">
              Αυτόματη ενημέρωση. Πατήστε «Save» κάτω για οριστική αποθήκευση.
            </p>
            <button
              type="button"
              onClick={finishEdit}
              className="text-[11px] px-2 py-1 border border-zinc-300 hover:bg-zinc-50"
              data-testid={`${testIdBase}-done-btn`}
            >
              Τέλος
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`border px-3 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
            hasUnsaved ? 'border-amber-300 bg-amber-50/40 text-zinc-800'
              : isOverridden ? 'border-blue-200 bg-blue-50/30 text-zinc-800'
              : 'border-zinc-200 bg-zinc-50/60 text-zinc-700'
          }`}
          data-testid={`${testIdBase}-preview`}
        >
          {display}
        </div>
      )}
      <p className="text-[10px] text-zinc-400 mt-1 italic">
        {hasUnsaved
          ? 'Έχετε αλλαγές που δεν έχουν αποθηκευτεί. Πατήστε «Save» κάτω για να αποθηκευτούν.'
          : isOverridden
            ? 'Custom override για αυτή τη συνέλευση. Πατήστε «Επαναφορά» για να επιστρέψετε στο αυτόματο κείμενο.'
            : 'Χρησιμοποιείται το κεντρικό πρότυπο. Μπορείτε να το επεξεργαστείτε μόνο για αυτή τη συνέλευση.'}
      </p>
    </div>
  );
}

// Phase 207 — Action panel rendered above the meeting body. Shows the next
// allowed transitions plus the per-manager approval / signature checklist.
function WorkflowPanel({ mtg, buildingId, isAdmin, onChanged, onSubmitDraftClick }) {
  const status = mtg.status || 'draft';
  const approvals = mtg.approvals || [];
  const [busy, setBusy] = useState(false);
  const [noteFor, setNoteFor] = useState({}); // { managerName: 'note text' }
  // Phase 712 — Manual approval dialog state. Lets the admin close the
  // approval loop without sending emails (verbal/in-person approval).
  const [manualApproveOpen, setManualApproveOpen] = useState(false);
  const [manualApproveNote, setManualApproveNote] = useState('');
  // Phase 735.12 — Filter chip: All Requests / #1 / #2 / …
  const [requestFilter, setRequestFilter] = useState('all');
  // Phase 735.18 — Approval History is a collapsible accordion (collapsed by default).
  const [historyOpen, setHistoryOpen] = useState(false);
  const requestNumbers = useMemo(
    () => Array.from(new Set(approvals.map(a => Number(a.request_number) || 1))).sort((a, b) => a - b),
    [approvals],
  );
  const filteredApprovals = useMemo(
    () => (requestFilter === 'all'
      ? approvals
      : approvals.filter(a => Number(a.request_number || 1) === Number(requestFilter))),
    [approvals, requestFilter],
  );

  // Phase 735.21 — Deduplicate active approval rows. For each recipient
  // (grouped by name — falling back to email when name is empty), keep ONLY
  // the row with the highest `request_number`. Older rows for the same
  // recipient are marked as "superseded" and pushed into the Approval
  // History accordion so the audit trail remains complete without cluttering
  // the active view.
  const { activeApprovals, supersededTokens } = useMemo(() => {
    const latestByPerson = new Map(); // key → approval row
    for (const a of approvals) {
      const key = ((a.name || '').trim().toLowerCase()
        || (a.email || '').trim().toLowerCase());
      if (!key) continue;
      const prev = latestByPerson.get(key);
      const cur = Number(a.request_number || 1);
      if (!prev || Number(prev.request_number || 1) < cur) {
        latestByPerson.set(key, a);
      }
    }
    const activeSet = new Set(Array.from(latestByPerson.values()));
    const superseded = new Set();
    for (const a of approvals) {
      if (!activeSet.has(a)) {
        // Use approval_token when available for a stable identity, otherwise
        // fall back to a composite key (name#req#).
        superseded.add(a.approval_token || `${a.name || ''}#${a.request_number || 1}`);
      }
    }
    // Keep active rows in the same relative order as the source array.
    const active = approvals.filter(a => activeSet.has(a));
    return { activeApprovals: active, supersededTokens: superseded };
  }, [approvals]);

  const isSuperseded = (a) => supersededTokens.has(
    a.approval_token || `${a.name || ''}#${a.request_number || 1}`,
  );

  const post = async (path, body) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}${path}`, body || {});
      // Phase 207h — Submit-for-approval returns the list of mocked emails sent
      // to managers. Surface them in a richer toast so the admin sees who got
      // the approval link.
      if (res.data?.emails_sent && res.data.emails_sent.length > 0) {
        const recipients = res.data.emails_sent
          .map(e => `${e.manager_name}${e.to_email ? ` (${e.to_email})` : ' (no email)'}`)
          .join(', ');
        const mode = res.data.delivery_mode === 'mocked' ? ' [MOCKED]' : '';
        toast.success(`Email${mode} στάλθηκε σε: ${recipients}`, { duration: 6000 });
      } else {
        toast.success(res.data?.message || 'OK');
      }
      onChanged && onChanged();
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error_code === 'no_active_committee') {
        toast.error('Αυτή η συνέλευση δεν έχει active μέλη επιτροπής. Επεξεργαστείτε τη και προσθέστε committee members με active=true.', { duration: 7000 });
      } else if (detail && typeof detail === 'object' && detail.error_code === 'no_managers') {
        toast.error('Δεν υπάρχουν διαχειριστές. Πρέπει πρώτα να ορίσετε διαχειριστική επιτροπή.', { duration: 7000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : err?.response?.data?.message || 'Σφάλμα');
      }
    } finally {
      setBusy(false);
    }
  };

  const submitForApproval = () => {
    // Phase 207m — Always go through the approval email composition modal.
    if (onSubmitDraftClick) {
      onSubmitDraftClick();
    } else {
      post('/submit-for-approval');
    }
  };
  const approveAs = (name) => post('/approve', { manager_name: name, action: 'approve' });
  const rejectAs = (name) => post('/approve', { manager_name: name, action: 'reject', note: noteFor[name] || '' });
  const requestChanges = (name) => post('/approve', { manager_name: name, action: 'request_changes', note: noteFor[name] || '' });
  const signAs = (name) => post('/sign', { manager_name: name });
  const sendToOwners = () => post('/send-to-owners');
  const resetToDraft = () => post('/reset-to-draft');

  // Phase 712 — Manual approve: closes the approval loop without sending
  // any emails. Allowed from draft / pending_approval / needs_changes.
  const canManualApprove = isAdmin && ['draft', 'pending_approval', 'needs_changes'].includes(status);
  const openManualApprove = () => {
    setManualApproveNote('');
    setManualApproveOpen(true);
  };
  const confirmManualApprove = async () => {
    await post('/manual-approve', { note: manualApproveNote || '' });
    setManualApproveOpen(false);
    setManualApproveNote('');
  };

  // Phase 735.8 / 735.18 — Send reminder. Accepts optional `recipient` name
  // so the admin can nudge a single pending recipient from the row-level
  // action button. When called without an argument, reminds all pending.
  const sendReminder = async (recipient = null) => {
    if (busy) return;
    setBusy(true);
    try {
      const body = recipient ? { recipients: [recipient] } : {};
      const res = await axios.post(
        `${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}/send-reminder`,
        body,
      );
      const dc = res.data?.delivery_counts || {};
      const who = recipient ? recipient : `${dc.sent || dc.failed || dc.mocked || 0} recipient(s)`;
      if (dc.sent) toast.success(`Reminder sent to ${who}.`);
      else if (dc.failed) toast.error(`Reminder failed for ${who}.`);
      else if (dc.mocked) toast.warning(`Reminder logged as mock — Brevo key not configured.`);
      onChanged && onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to send reminder');
    } finally {
      setBusy(false);
    }
  };

  // Phase 735.18 — Re-send a fresh approval request to ONE specific
  // recipient (used after they clicked "Request Changes" and the admin
  // has updated the meeting). Never spams other recipients; never
  // re-adds approved recipients.
  const resendToRecipient = async (recipientName) => {
    if (busy || !recipientName) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}/send-additional-approvals`,
        { include_recipients: [recipientName] },
      );
      const reqNum = res.data?.request_number;
      const dc = res.data?.delivery_counts || {};
      if (dc.sent) toast.success(`Approval #${reqNum} sent to ${recipientName}.`);
      else if (dc.failed) toast.error(`Failed to send Approval #${reqNum} to ${recipientName}.`);
      else if (dc.mocked) toast.warning(`Approval #${reqNum} to ${recipientName} logged as mock.`);
      onChanged && onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to resend');
    } finally {
      setBusy(false);
    }
  };

  // Phase 735.12 — Send to Committee Again — one-click helper that opens a
  // brand-new Approval Request (auto-assigned Request #N) and dispatches an
  // email to every ACTIVE committee member. Never changes meeting status.
  const sendToCommitteeAgain = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}/send-additional-approvals`,
        { include_committee: true },
      );
      const dc = res.data?.delivery_counts || {};
      const reqNum = res.data?.request_number;
      if (dc.sent) toast.success(`Approval Request #${reqNum}: sent to ${dc.sent} committee member(s).`);
      else if (dc.failed) toast.error(`Approval Request #${reqNum} failed for ${dc.failed} member(s).`);
      else if (dc.mocked) toast.warning(`Approval Request #${reqNum} logged as mock — Brevo key not configured.`);
      onChanged && onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to send committee request');
    } finally {
      setBusy(false);
    }
  };

  const shareUrl = mtg.share_token
    ? `${window.location.origin}${process.env.REACT_APP_BACKEND_URL ? '' : ''}/api/public/meetings/${mtg.share_token}/pdf`
    : '';
  const fullShareUrl = mtg.share_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/meetings/${mtg.share_token}/pdf`
    : '';

  return (
    <div className="bg-white border border-zinc-200" data-testid="workflow-panel">
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
        <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Ροή Έγκρισης</h3>
        <StatusBadge status={status} onSubmitDraft={status === 'draft' && isAdmin ? submitForApproval : null} busy={busy} />
      </div>

      {/* Phase 207k — DRAFT helper text only (button moved to StatusBadge above) */}
      {status === 'draft' && isAdmin && (
        <div className="px-5 py-3 text-xs text-zinc-500 flex items-start justify-between gap-3 flex-wrap">
          <span className="flex-1 min-w-[220px]">
            Πατήστε «Αποστολή για έγκριση» πάνω για να σταλεί email αυτόματα στα active μέλη της επιτροπής,
            ή «Έγκριση χειροκίνητα» αν η έγκριση έγινε εκτός email (π.χ. δια ζώσης ή τηλεφωνικά).
          </span>
          {/* Phase 712 — Manual approval entry point (DRAFT). */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={openManualApprove}
            className="rounded-none h-7 text-[11px] uppercase tracking-wider border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-semibold"
            data-testid="manual-approve-btn"
          >
            <CheckCircle size={14} className="mr-1" weight="fill" />
            Έγκριση χειροκίνητα
          </Button>
        </div>
      )}

      {/* Phase 712 — Manual approval entry point also available while the
          meeting is in pending_approval / needs_changes so the admin can
          close the loop without waiting on email replies. */}
      {(status === 'pending_approval' || status === 'needs_changes') && canManualApprove && (
        <div className="px-5 py-2 border-b border-zinc-100 flex items-center justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={openManualApprove}
            className="rounded-none h-7 text-[11px] uppercase tracking-wider border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-semibold"
            data-testid="manual-approve-btn-pending"
            title="Έγκριση χειροκίνητα χωρίς αποστολή email"
          >
            <CheckCircle size={14} className="mr-1" weight="fill" />
            Έγκριση χειροκίνητα
          </Button>
        </div>
      )}

      {/* Step 2 — Per-manager decision rows. Visible from pending_approval onwards.
          Phase 735.6 — Extended with full approval history columns (Email,
          Sent, Response Date, Comment) and a "Print Approval Report" button.
          Phase 735.12 — Approval Request tracking (Request #N column + filter
          chips + always-available "Send to Committee Again" / "Send to
          Additional Recipients" actions). */}
      {(status === 'pending_approval' || status === 'needs_changes' || status === 'rejected' || status === 'approved' || status === 'pending_signature' || status === 'completed' || status === 'sent') && approvals.length > 0 && (
        <div className="px-5 py-3">
          {/* Phase 735.21 — Active Approvals block (always visible). Shows
              ONLY the latest approval request per recipient. Superseded rows
              live inside the "Approval History" accordion below with a
              "Superseded" badge so the full audit trail is preserved. */}
          <div className="mb-3" data-testid="active-approvals-section">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                Ενεργές Εγκρίσεις ({activeApprovals.length})
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                {isAdmin && (
                  <button
                    type="button"
                    onClick={sendToCommitteeAgain}
                    disabled={busy}
                    className="text-xs font-semibold text-blue-800 hover:text-blue-900 border border-blue-300 bg-blue-50 px-2.5 py-1 hover:bg-blue-100 transition-colors rounded-none disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="send-to-committee-again-btn"
                    title="Δημιουργία νέου Approval Request και αποστολή σε όλα τα ενεργά μέλη επιτροπής"
                  >
                    <EnvelopeSimple size={12} />Send to Committee Again
                  </button>
                )}
                {isAdmin && onSubmitDraftClick && (
                  <button
                    type="button"
                    onClick={onSubmitDraftClick}
                    disabled={busy}
                    className="text-xs font-semibold text-indigo-800 hover:text-indigo-900 border border-indigo-300 bg-indigo-50 px-2.5 py-1 hover:bg-indigo-100 transition-colors rounded-none disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="send-to-additional-recipients-btn"
                    title="Επιλογή διαμερισμάτων ή/και custom emails για νέο Approval Request"
                  >
                    <EnvelopeSimple size={12} />Send to Additional Recipients
                  </button>
                )}
                {isAdmin && activeApprovals.some(a => (a.status || 'pending').toLowerCase() === 'pending' && (a.email || '').trim()) && (
                  <button
                    type="button"
                    onClick={() => sendReminder()}
                    disabled={busy}
                    className="text-xs font-semibold text-amber-800 hover:text-amber-900 border border-amber-300 bg-amber-50 px-2.5 py-1 hover:bg-amber-100 transition-colors rounded-none disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="send-reminder-btn"
                    title="Send reminder to all pending recipients"
                  >
                    <EnvelopeSimple size={12} />Send Reminder
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    Promise.resolve(openPdfInBrowserUtil(
                      `/buildings/${buildingId}/meetings/${mtg.meeting_id}/approval-report/pdf`,
                      {},
                    )).catch((e) => toast.error(e?.message || 'PDF failed'));
                  }}
                  className="text-xs font-semibold text-zinc-700 hover:text-zinc-900 border border-zinc-300 px-2.5 py-1 hover:bg-zinc-50 transition-colors rounded-none inline-flex items-center gap-1"
                  data-testid="print-approval-report-btn"
                >
                  <FilePdf size={12} />Print Approval Report
                </button>
              </div>
            </div>
            <div className="border border-zinc-100 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">Req #</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Παραλήπτης</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Διαμ.</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Email</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Sent</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Απόφαση</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Response</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Comment / History</th>
                    <th className="text-right px-3 py-1.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {activeApprovals.map((a, idx) => (
                    <tr key={`active-${a.approval_token || a.name}-${idx}`} data-testid={`approval-row-${a.name}`} className="align-top">
                      <td className="px-3 py-2 font-mono text-zinc-500 whitespace-nowrap">
                        <span className="inline-block px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 text-[10px] font-semibold text-zinc-700">
                          #{a.request_number || 1}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium min-w-[140px]">{a.name}</td>
                      <td className="px-3 py-2 font-mono text-zinc-500">{a.apartment_number || '—'}</td>
                      <td className="px-3 py-2 font-mono text-zinc-600 truncate max-w-[180px]">{a.email || '—'}</td>
                      <td className="px-3 py-2 font-mono text-zinc-500">{a.sent_at ? formatDateDMY((a.sent_at || '').split('T')[0]) : '—'}</td>
                      <td className="px-3 py-2">
                        {a.status === 'approved' ? (
                          <span className="text-emerald-700 inline-flex items-center gap-1" data-testid={`decision-${a.name}`}>
                            <CheckCircle size={13} weight="fill" />
                            {a.decided_via === 'manual_admin' ? 'Approved (Manual)' : 'Approved'}
                          </span>
                        ) : a.status === 'rejected' ? (
                          <span className="text-red-700 inline-flex items-center gap-1" data-testid={`decision-${a.name}`}>
                            <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>Rejected
                          </span>
                        ) : a.status === 'changes_requested' ? (
                          <span className="text-amber-700 inline-flex items-center gap-1" data-testid={`decision-${a.name}`}>
                            <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>Changes Requested
                          </span>
                        ) : (
                          <span className="text-zinc-500 inline-flex items-center gap-1" data-testid={`decision-${a.name}`}>
                            <span className="inline-block w-2 h-2 rounded-full bg-zinc-400"></span>Pending
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-zinc-500">{(a.decided_at || a.approved_at) ? formatDateDMY(((a.decided_at || a.approved_at) || '').split('T')[0]) : '—'}</td>
                      <td className="px-3 py-2 text-zinc-600 italic max-w-[220px] whitespace-pre-wrap">
                        {a.note || ''}
                        {(a.reminders || []).length > 0 && (
                          <div className="mt-1 pt-1 border-t border-zinc-100 not-italic text-[10px] text-zinc-500" data-testid={`reminders-${a.name}`}>
                            {(a.reminders || []).map((r, i) => (
                              <div key={i}>
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1 align-middle"></span>
                                Reminder {i + 1}: {r.at ? formatDateDMY((r.at || '').split('T')[0]) : ''} {r.at ? (r.at.split('T')[1] || '').slice(0, 5) : ''} ({r.delivery || 'unknown'})
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {isAdmin && (a.status || 'pending').toLowerCase() === 'pending' && (a.email || '').trim() ? (
                          <button
                            type="button"
                            onClick={() => sendReminder(a.name)}
                            disabled={busy}
                            className="text-[10px] font-semibold text-amber-800 border border-amber-300 bg-amber-50 px-2 py-0.5 hover:bg-amber-100 transition-colors disabled:opacity-50"
                            title="Send reminder to this recipient only"
                            data-testid={`row-reminder-${a.name}`}
                          >
                            Reminder
                          </button>
                        ) : isAdmin && a.status === 'changes_requested' ? (
                          <button
                            type="button"
                            onClick={() => resendToRecipient(a.name)}
                            disabled={busy}
                            className="text-[10px] font-semibold text-indigo-800 border border-indigo-300 bg-indigo-50 px-2 py-0.5 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                            title="Send the updated meeting to this recipient only for a new approval"
                            data-testid={`row-resend-${a.name}`}
                          >
                            Re-send
                          </button>
                        ) : a.status === 'approved' ? (
                          <span className="text-[10px] text-emerald-600 italic">Already Approved</span>
                        ) : a.status === 'rejected' ? (
                          <span className="text-[10px] text-red-600 italic">Rejected</span>
                        ) : (
                          <span className="text-[10px] text-zinc-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Inline action buttons (admin) only while still pending — kept
                inside the Active Approvals block so admin approve/reject/
                request-changes controls operate on the LATEST request per
                recipient (never a stale, superseded row). */}
            {status === 'pending_approval' && isAdmin && (
              <div className="mt-3 border border-zinc-100 divide-y divide-zinc-100">
                {activeApprovals.filter(a => a.status !== 'approved' && a.status !== 'rejected' && a.status !== 'changes_requested').map((a) => (
                  <div key={`act-${a.approval_token || a.name}`} className="px-3 py-2 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] text-zinc-500 flex-1 min-w-[120px]">{a.name}</span>
                    <Input
                      value={noteFor[a.name] || ''}
                      onChange={(e) => setNoteFor({ ...noteFor, [a.name]: e.target.value })}
                      placeholder="Comment (required for reject / changes)"
                      className="h-7 w-56 text-xs rounded-none"
                    />
                    <Button
                      onClick={() => {
                        if (!(noteFor[a.name] || '').trim()) { toast.error('Απαιτείται σχόλιο για το request changes'); return; }
                        requestChanges(a.name);
                      }}
                      disabled={busy} size="sm" variant="outline"
                      className="h-7 rounded-none text-xs" data-testid={`request-changes-${a.name}`}
                    >Changes</Button>
                    <Button
                      onClick={() => {
                        if (!(noteFor[a.name] || '').trim()) { toast.error('Απαιτείται σχόλιο για το reject'); return; }
                        rejectAs(a.name);
                      }}
                      disabled={busy} size="sm" variant="outline"
                      className="h-7 rounded-none text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid={`reject-${a.name}`}
                    >Reject</Button>
                    <Button onClick={() => approveAs(a.name)} disabled={busy} size="sm" className="h-7 rounded-none bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`approve-${a.name}`}>Approve</Button>
                  </div>
                ))}
              </div>
            )}
            {status === 'pending_signature' && isAdmin && (
              <div className="mt-3 border border-zinc-100 divide-y divide-zinc-100">
                {activeApprovals.filter(a => !a.signed).map((a) => (
                  <div key={`sig-${a.name}`} className="px-3 py-2 flex items-center gap-2">
                    <span className="text-[11px] text-zinc-500 flex-1">{a.name}</span>
                    <Button onClick={() => signAs(a.name)} disabled={busy} size="sm" className="h-7 rounded-none bg-orange-600 hover:bg-orange-700 text-xs" data-testid={`sign-${a.name}`}>Υπογραφή</Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Phase 735.21 — Approval History accordion (collapsed by default).
              Shows ALL approval rows (including superseded ones tagged with a
              "Superseded" badge) so the full multi-round audit trail is
              preserved without cluttering the active view. */}
          <button
            type="button"
            onClick={() => setHistoryOpen(v => !v)}
            className="w-full flex items-center justify-between gap-2 py-2 text-left hover:bg-zinc-50 transition-colors -mx-2 px-2"
            data-testid="approval-history-toggle"
          >
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-2">
              Ιστορικό Εγκρίσεων ({approvals.length})
            </span>
            <span className="text-zinc-400 text-sm">{historyOpen ? '▾' : '▸'}</span>
          </button>
          {historyOpen && (
          <div className="mt-2">
          {requestNumbers.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-2" data-testid="request-filter-chips">
              <button
                type="button"
                onClick={() => setRequestFilter('all')}
                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border transition-colors ${
                  requestFilter === 'all'
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50'
                }`}
                data-testid="request-filter-all"
              >
                All ({approvals.length})
              </button>
              {requestNumbers.map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRequestFilter(n)}
                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border transition-colors ${
                    Number(requestFilter) === n
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50'
                  }`}
                  data-testid={`request-filter-${n}`}
                >
                  Approval #{n} ({approvals.filter(a => Number(a.request_number || 1) === n).length})
                </button>
              ))}
            </div>
          )}
          <div className="border border-zinc-100 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold">Req #</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Παραλήπτης</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Διαμ.</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Email</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Sent</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Απόφαση</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Response</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Comment / History</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
            {filteredApprovals.map((a, idx) => (
              <tr key={`hist-${a.approval_token || a.name}-${idx}`} data-testid={`approval-history-row-${a.name}-${a.request_number || 1}`} className={`align-top ${isSuperseded(a) ? 'bg-zinc-50/60' : ''}`}>
                <td className="px-3 py-2 font-mono text-zinc-500 whitespace-nowrap">
                  <span className="inline-block px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 text-[10px] font-semibold text-zinc-700">
                    #{a.request_number || 1}
                  </span>
                  {isSuperseded(a) && (
                    <span
                      className="ml-1 inline-block px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-[9px] uppercase tracking-wider font-semibold text-amber-700"
                      data-testid={`superseded-badge-${a.name}-${a.request_number || 1}`}
                      title="Αντικαταστάθηκε από νεότερο Approval Request"
                    >
                      Superseded
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-medium min-w-[140px]">{a.name}</td>
                <td className="px-3 py-2 font-mono text-zinc-500">{a.apartment_number || '—'}</td>
                <td className="px-3 py-2 font-mono text-zinc-600 truncate max-w-[180px]">{a.email || '—'}</td>
                <td className="px-3 py-2 font-mono text-zinc-500">{a.sent_at ? formatDateDMY((a.sent_at || '').split('T')[0]) : '—'}</td>
                <td className="px-3 py-2">
                {a.status === 'approved' ? (
                  <span className="text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle size={13} weight="fill" />
                    {a.decided_via === 'manual_admin' ? 'Approved (Manual)' : 'Approved'}
                  </span>
                ) : a.status === 'rejected' ? (
                  <span className="text-red-700 inline-flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>Rejected
                  </span>
                ) : a.status === 'changes_requested' ? (
                  <span className="text-amber-700 inline-flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>Changes Requested
                  </span>
                ) : (
                  <span className="text-zinc-500 inline-flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-zinc-400"></span>Pending
                  </span>
                )}
                </td>
                <td className="px-3 py-2 font-mono text-zinc-500">{(a.decided_at || a.approved_at) ? formatDateDMY(((a.decided_at || a.approved_at) || '').split('T')[0]) : '—'}</td>
                <td className="px-3 py-2 text-zinc-600 italic max-w-[220px] whitespace-pre-wrap">
                  {a.note || ''}
                  {(a.reminders || []).length > 0 && (
                    <div className="mt-1 pt-1 border-t border-zinc-100 not-italic text-[10px] text-zinc-500">
                      {(a.reminders || []).map((r, i) => (
                        <div key={i}>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1 align-middle"></span>
                          Reminder {i + 1}: {r.at ? formatDateDMY((r.at || '').split('T')[0]) : ''} {r.at ? (r.at.split('T')[1] || '').slice(0, 5) : ''} ({r.delivery || 'unknown'})
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
              </tbody>
            </table>
          </div>
        </div>
          )}
        </div>
      )}

      {/* Phase 208 — Reset to draft when rejected / needs_changes */}
      {(status === 'rejected' || status === 'needs_changes') && isAdmin && (
        <div className="px-5 py-4 border-t border-zinc-100 bg-orange-50/40 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-orange-800">
            {status === 'rejected'
              ? 'Τα πρακτικά απορρίφθηκαν. Επεξεργαστείτε τα και επανυποβάλετε.'
              : 'Ζητήθηκαν αλλαγές. Επεξεργαστείτε τα και επανυποβάλετε για έγκριση.'}
          </p>
          <Button onClick={resetToDraft} disabled={busy} size="sm" className="bg-zinc-900 hover:bg-zinc-800 rounded-none text-xs" data-testid="reset-to-draft-btn">
            Επαναφορά σε Πρόχειρο
          </Button>
        </div>
      )}

      {/* Step 4 — Send to owners */}
      {status === 'completed' && isAdmin && (
        <div className="px-5 py-4 flex items-center justify-between gap-4 border-t border-zinc-100">
          <p className="text-xs text-zinc-500">Όλες οι υπογραφές ολοκληρώθηκαν. Δημιουργήστε share link για αποστολή στους ιδιοκτήτες.</p>
          <Button onClick={sendToOwners} disabled={busy} size="sm" className="bg-blue-600 hover:bg-blue-700 rounded-none" data-testid="send-to-owners-btn">
            Αποστολή σε ιδιοκτήτες
          </Button>
        </div>
      )}

      {/* SENT — show share link */}
      {status === 'sent' && fullShareUrl && (
        <div className="px-5 py-4 border-t border-zinc-100 bg-blue-50/40">
          <p className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-2">Δημόσιος σύνδεσμος (για ιδιοκτήτες)</p>
          <div className="flex items-center gap-2">
            <Input value={fullShareUrl} readOnly className="flex-1 text-xs rounded-none font-mono bg-white" data-testid="share-link-input" />
            <Button
              size="sm"
              variant="outline"
              className="rounded-none"
              onClick={() => { navigator.clipboard.writeText(fullShareUrl); toast.success('Αντιγράφηκε'); }}
              data-testid="copy-share-link"
            >
              Αντιγραφή
            </Button>
            <Button
              size="sm"
              className="rounded-none bg-zinc-900"
              onClick={() => window.open(fullShareUrl, '_blank')}
              data-testid="open-share-link"
            >
              Άνοιγμα
            </Button>
          </div>
          <p className="text-[10px] text-blue-600 mt-2">
            Στείλτε αυτό το link με WhatsApp / email / SMS όπου θέλετε. Μόνο πρακτικά σε κατάσταση «Απεσταλμένο» είναι προσβάσιμα.
          </p>
        </div>
      )}

      {/* Audit log (last 5) */}
      {(mtg.audit_log || []).length > 0 && (
        <details className="px-5 py-3 border-t border-zinc-100">
          <summary className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold cursor-pointer">
            Ιστορικό ({(mtg.audit_log || []).length})
          </summary>
          <div className="mt-2 space-y-1 text-xs text-zinc-600">
            {(mtg.audit_log || []).slice(-10).reverse().map((e, idx) => (
              <div key={idx} className="font-mono text-[11px] flex gap-3">
                <span className="text-zinc-400">{e.timestamp ? formatDateDMY(e.timestamp.split('T')[0]) : '—'}</span>
                <span className="font-semibold uppercase">{e.action}</span>
                <span>{e.actor_name || e.actor}</span>
                {e.manager && <span className="text-zinc-500">→ {e.manager}</span>}
                {e.note && <span className="text-zinc-500 italic">"{e.note}"</span>}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Phase 712 — Manual Approve confirmation dialog. Optional note
          field; pressing Confirm calls /manual-approve which sets the
          meeting to APPROVED without sending any emails. */}
      <Dialog open={manualApproveOpen} onOpenChange={setManualApproveOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="manual-approve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CheckCircle size={18} weight="fill" className="text-emerald-600" />
              Χειροκίνητη Έγκριση Πρακτικών
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zinc-700">
            <p>
              Θέλετε να εγκρίνετε τα πρακτικά <strong>χωρίς να σταλεί email</strong>;
              Όλοι οι διαχειριστές θα σημανθούν ως <em>Approved (Manual)</em> με ένδειξη
              «χειροκίνητη έγκριση από admin» στο ιστορικό.
            </p>
            <div>
              <Label htmlFor="manual-approve-note" className="text-[11px] uppercase text-zinc-500">
                Σημείωση (προαιρετικό)
              </Label>
              <Textarea
                id="manual-approve-note"
                value={manualApproveNote}
                onChange={(e) => setManualApproveNote(e.target.value)}
                placeholder="π.χ. Εγκρίθηκε προφορικά στο γραφείο 25/06/2026"
                rows={3}
                className="rounded-none mt-1 text-sm"
                data-testid="manual-approve-note"
              />
            </div>
            <p className="text-[11px] text-zinc-500">
              Δεν θα σταλεί κανένα email. Το meeting θα μεταβεί απευθείας σε «Εγκεκριμένο».
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-none"
              onClick={() => setManualApproveOpen(false)}
              disabled={busy}
              data-testid="manual-approve-cancel"
            >
              Άκυρο
            </Button>
            <Button
              type="button"
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={confirmManualApprove}
              disabled={busy}
              data-testid="manual-approve-confirm"
            >
              <CheckCircle size={14} className="mr-1" weight="fill" />
              Επιβεβαίωση Χειροκίνητης Έγκρισης
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MeetingsTab({ buildingId, buildingName, apartments, isAdmin }) {
  // Phase 178 — Meetings module is Greek-only. We intentionally ignore the
  // global language toggle for this module: every label, template, topic,
  // and generated text is rendered in Greek regardless of the app language.
  useLanguage(); // keep hook order stable; values are not used here.
  const t = (key) => getTranslation('el', key);
  const language = 'el';
  const lang = 'el';
  // Phase 208c — Demo vs production lock. When `lockApprovedMeetings` is false
  // (demo), edit buttons remain enabled regardless of status; in production
  // any non-DRAFT meeting hides the edit affordances.
  const { lockApprovedMeetings } = useAppConfig();
  const canEditMeeting = (mtg) => {
    if (!isAdmin) return false;
    if (!lockApprovedMeetings) return true;
    return (mtg?.status || 'draft') === 'draft';
  };
  const [meetings, setMeetings] = useState([]);
  const [view, setView] = useState('list'); // list | form | detail
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingMeeting, setDeletingMeeting] = useState(null);
  const [agendaTemplates, setAgendaTemplates] = useState([]);
  const [topicCategories, setTopicCategories] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // Phase 207b — Inline modal for editing the central meeting text templates
  // (Legal Quorum & Committee Decision). Replaces the previous redirect to
  // /settings?section=meetingTemplates which felt like the user was kicked out.
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  // Phase 207m — Approval email composition modal state.
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalDialogMeeting, setApprovalDialogMeeting] = useState(null);

  const openApprovalDialog = (mtg) => {
    setApprovalDialogMeeting(mtg);
    setApprovalDialogOpen(true);
  };
  // Phase 209 — English translation dialog
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [translateMeeting, setTranslateMeeting] = useState(null);
  const openTranslateDialog = (mtg) => {
    setTranslateMeeting(mtg);
    setTranslateDialogOpen(true);
  };
  const [emailProps, setEmailProps] = useState({ pdfUrl: '', filename: '', defaultEmail: '', templateType: 'meeting', dynamicFields: {} });

  // Form state
  const [form, setForm] = useState({
    meeting_type: 'agm',
    date: new Date().toISOString().split('T')[0],
    time: '',
    end_time: '',
    location: '',
    attendance: [],
    topics: [{ title: '', decision: '' }],
    status: 'draft',
    quorum_text: '',
    committee_text: '',
    legal_text: '',
    closing_notes: '',
    include_financial_summary: false,
    // Phase 942 — Next Meeting fields directly editable on the form.
    next_meeting_date: '',
    next_meeting_time: '',
  });
  const [financialSnapshot, setFinancialSnapshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Phase 209e — Default closing-notes text fetched once from the backend.
  const [defaultClosingNotes, setDefaultClosingNotes] = useState('');
  useEffect(() => {
    axios
      .get(`${API}/meetings/closing-notes-default?lang=el`)
      .then((r) => setDefaultClosingNotes(r.data?.text || ''))
      .catch(() => {});
  }, []);

  const fetchMeetings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/meetings`);
      setMeetings(res.data || []);
    } catch { setMeetings([]); }
  }, [buildingId]);

  const fetchTemplatesAndCategories = useCallback(async () => {
    try {
      const [tRes, cRes] = await Promise.all([
        axios.get(`${API}/agenda-templates`, { withCredentials: true }),
        axios.get(`${API}/topic-categories`, { withCredentials: true }),
      ]);
      setAgendaTemplates(tRes.data || []);
      setTopicCategories(cRes.data || []);
    } catch {}
  }, []);

  // Phase 192 — Central meeting text templates (legal + committee), admin-
  // editable via Settings → Meeting Text Templates. Read-only previews inside
  // the meeting form use these values; the quorum paragraph stays 100% auto.
  const [meetingTemplates, setMeetingTemplates] = useState({ legal_text: '', committee_text: '' });
  const fetchMeetingTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/meeting-text-templates`, { withCredentials: true });
      setMeetingTemplates({
        legal_text: r.data?.legal_text || '',
        committee_text: r.data?.committee_text || '',
      });
    } catch {}
  }, []);

  // Phase 735.1 — Company Settings snapshot used by the Agenda Templates
  // variable resolver ({company_name}, {company_email}, {company_phone}).
  // The values live under `/api/company-settings` and change infrequently;
  // one fetch on mount is enough for the whole meeting session.
  const [companySettings, setCompanySettings] = useState({});
  const fetchCompanySettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/company-settings`, { withCredentials: true });
      setCompanySettings(r.data || {});
    } catch {}
  }, []);

  useEffect(() => { fetchMeetings(); fetchTemplatesAndCategories(); fetchMeetingTemplates(); fetchCompanySettings(); }, [fetchMeetings, fetchTemplatesAndCategories, fetchMeetingTemplates, fetchCompanySettings]);

  const resetForm = () => {
    const att = (apartments || []).map(a => ({
      apartment_id: a.apartment_id,
      apartment_number: a.apartment_number,
      owner_name: a.owner_name || '',
      share_percentage: a.share_percentage || 0,
      present: false,
    }));
    setForm({
      meeting_type: 'agm',
      date: new Date().toISOString().split('T')[0],
      time: '',
      end_time: '',
      location: '',
      attendance: att,
      committee: COMMITTEE_ROLES.map(r => ({ role: r, apartment_id: '', custom_name: '', active: true })),
      topics: [{ title: '', decision: '', category_id: '' }],
      status: 'draft',
      quorum_text: '',
      committee_text: '',
      legal_text: '',
      // Phase 209e — Pre-fill default for new meetings; user can edit or replace freely.
      closing_notes: defaultClosingNotes,
      include_financial_summary: false,
    });
    setFinancialSnapshot(null);
  };

  const openNewMeeting = () => {
    setEditingMeeting(null);
    resetForm();
    // Phase 735.4 — Fresh form starts with only topic #0 expanded.
    setExpandedTopics(new Set([0]));
    setView('form');
  };

  const openEditMeeting = async (mtg) => {
    setEditingMeeting(mtg);
    setForm({
      meeting_type: mtg.meeting_type || 'agm',
      date: mtg.date || '',
      time: mtg.time || '',
      end_time: mtg.end_time || '',
      location: mtg.location || '',
      attendance: mtg.attendance || [],
      committee: mtg.committee?.length
        ? mtg.committee.map(m => ({ ...m, active: m.active !== false }))
        : COMMITTEE_ROLES.map(r => ({ role: r, apartment_id: '', custom_name: '', active: true })),
      topics: mtg.topics?.length ? mtg.topics.map(t => ({ title: t.title || '', decision: t.decision || '', category_id: t.category_id || '' })) : [{ title: '', decision: '', category_id: '' }],
      status: mtg.status || 'draft',
      quorum_text: mtg.quorum_text || '',
      committee_text: mtg.committee_text || '',
      legal_text: mtg.legal_text || '',
      // Phase 209e — Empty saved value gets the default so the user can iterate on it.
      closing_notes: (mtg.closing_notes && mtg.closing_notes.trim()) || defaultClosingNotes,
      include_financial_summary: !!mtg.include_financial_summary,
      // Phase 942 — Preload Next Meeting fields so the form date-picker
      // reflects the current value.
      next_meeting_date: mtg.next_meeting_date || '',
      next_meeting_time: mtg.next_meeting_time || '',
    });
    // Phase 735.4 — Existing meetings open with all topics collapsed so the
    // form loads compact; the user expands whichever they want to edit.
    setExpandedTopics(new Set());
    setFinancialSnapshot(mtg.financial_snapshot || null);
    setView('form');
  };

  const openViewMeeting = async (mtg) => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}`);
      setEditingMeeting(res.data);
    } catch {
      setEditingMeeting(mtg);
    }
    setView('detail');
  };

  // Attendance
  const togglePresent = (aptId) => {
    setForm(prev => ({
      ...prev,
      attendance: prev.attendance.map(a =>
        a.apartment_id === aptId ? { ...a, present: !a.present } : a
      ),
    }));
  };

  const quorum = (() => {
    const total = form.attendance.reduce((s, a) => s + (a.share_percentage || 0), 0);
    const present = form.attendance.filter(a => a.present).reduce((s, a) => s + (a.share_percentage || 0), 0);
    return total > 0 ? (present / total * 100) : 0;
  })();

  // Attendance stats
  const totalApts = form.attendance.length;
  const presentCount = form.attendance.filter(a => a.present).length;
  const absentCount = totalApts - presentCount;
  const presentPct = totalApts > 0 ? (presentCount / totalApts * 100).toFixed(2) : '0.00';
  const absentPct = totalApts > 0 ? (absentCount / totalApts * 100).toFixed(2) : '0.00';

  // Compute duration from start/end time
  const computeDuration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let diff = (eh * 60 + em) - (sh * 60 + sm);
    if (diff < 0) diff += 24 * 60;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    if (language === 'el') {
      return `${hours} ώρ${hours !== 1 ? 'ες' : 'α'} και ${mins} λεπτά`;
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} and ${mins} minute${mins !== 1 ? 's' : ''}`;
  };

  // Auto-generate quorum text
  // Phase 191/194 — Branches on quorum >= 50 vs < 50. When no quorum, the text
  // must include the concrete scheduled next-meeting date (legally required).
  // If the admin hasn't picked the next date yet, a placeholder "[ΗΜΕΡΟΜΗΝΙΑ]"
  // is rendered so the user sees exactly what's missing.
  const generateQuorumText = () => {
    const duration = computeDuration(form.time, form.end_time);
    const pctInt = Math.round(quorum);
    const hasQuorum = quorum >= 50;
    const nextRaw = form.next_meeting_date || editingMeeting?.next_meeting_date || '';
    const nextFmt = nextRaw ? (() => { const p = nextRaw.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : nextRaw; })() : '[ΗΜΕΡΟΜΗΝΙΑ]';
    if (language === 'el') {
      if (hasQuorum) {
        return `Αφού διαπιστώθηκε η απαιτούμενη απαρτία της συνέλευσης (παρόντα/εκπροσωπούμενα διαμερίσματα: ${presentCount} από ${totalApts}, ποσοστό ${quorum.toFixed(2)}%), επισυνάπτεται παρουσιολόγιο (Επισυναπτόμενο 1). Η Ετήσια Γενική Συνέλευση διήρκεσε ${duration || '__ ώρες και __ λεπτά'}, από τις ${form.time || '[ΩΡΑ ΕΝΑΡΞΗΣ]'} μέχρι τις ${form.end_time || '[ΩΡΑ ΛΗΞΗΣ]'}.`;
      }
      return `Δεν διαπιστώθηκε η απαιτούμενη απαρτία της συνέλευσης, καθώς το ποσοστό παρουσίας ανήλθε σε ${pctInt}%.\n\nΩς εκ τούτου, η συνέλευση δεν δύναται να λάβει έγκυρες αποφάσεις.\n\nΑποφασίστηκε όπως συγκληθεί νέα συνέλευση την ${nextFmt}.`;
    }
    if (hasQuorum) {
      return `Having established the required quorum of the assembly (present/represented apartments: ${presentCount} out of ${totalApts}, percentage ${quorum.toFixed(2)}%), the attendance register is attached (Attachment 1). The Annual General Meeting lasted ${duration || '__ hours and __ minutes'}, from ${form.time || '[START TIME]'} to ${form.end_time || '[END TIME]'}.`;
    }
    return `The required quorum was not established, as the attendance rate reached only ${pctInt}%.\n\nTherefore, the assembly cannot take valid decisions.\n\nIt was decided that a new meeting will be convened on ${nextFmt}.`;
  };

  // Auto-generate legal text
  const generateLegalText = () => {
    if (language === 'el') {
      return 'Απαρτία σύμφωνα με το άρθρο 38Θ του Περί Ακίνητης Ιδιοκτησίας Νόμου υπάρχει όταν παρίστανται, προσωπικά ή με πληρεξούσιο, πρόσωπα στα οποία ανήκει τουλάχιστον το 50% της κοινόκτητης ιδιοκτησίας.';
    }
    return 'A quorum, in accordance with Article 38Θ of the Immovable Property Law, exists when persons holding at least 50% of the common property are present, either in person or by proxy.';
  };

  // Auto-generate committee text
  const generateCommitteeText = () => {
    if (language === 'el') {
      return 'Μετά από ομόφωνη απόφαση, αποφασίστηκε όπως η Διαχειριστική Επιτροπή παραμείνει ως έχει:';
    }
    return 'By unanimous decision, it was decided that the Management Committee shall remain as follows:';
  };

  // Committee
  const updateCommitteeMember = (roleIdx, field, value) => {
    setForm(prev => ({
      ...prev,
      committee: prev.committee.map((m, i) => {
        if (i !== roleIdx) return m;
        if (field === 'apartment_id') {
          // When selecting an apartment, clear custom_name; when selecting '', keep custom_name
          return { ...m, apartment_id: value, custom_name: value ? '' : m.custom_name };
        }
        if (field === 'custom_name') {
          return { ...m, custom_name: value, apartment_id: '' };
        }
        return { ...m, [field]: value };
      }),
    }));
  };

  const addCommitteeMember = () => {
    setForm(prev => ({ ...prev, committee: [...prev.committee, { role: '', apartment_id: '', custom_name: '', active: true }] }));
  };

  const removeCommitteeMember = (idx) => {
    setForm(prev => ({ ...prev, committee: prev.committee.filter((_, i) => i !== idx) }));
  };

  const getCommitteeDisplay = (member) => {
    if (member.apartment_id) {
      const apt = (apartments || []).find(a => a.apartment_id === member.apartment_id);
      if (apt) return `${apt.owner_name || '-'} (${t('apartmentShort')} ${apt.apartment_number})`;
    }
    return member.custom_name || '-';
  };

  // Topics
  // Phase 735.4 — Topics & Decisions accordion. Tracks which topic rows are
  // expanded. Default: only the first topic is open. Clicking a header
  // toggles it (accordion: opening a new row collapses any previously open
  // one). Expand All / Collapse All override this behaviour.
  const [expandedTopics, setExpandedTopics] = useState(() => new Set([0]));
  const toggleTopicExpanded = (idx) => {
    setExpandedTopics(prev => {
      if (prev.has(idx)) {
        // Collapse the currently-open row without expanding anything else.
        const next = new Set(prev);
        next.delete(idx);
        return next;
      }
      // Accordion: opening idx replaces any previously-expanded row.
      return new Set([idx]);
    });
  };
  const expandAllTopics = () => setExpandedTopics(new Set(form.topics.map((_, i) => i)));
  const collapseAllTopics = () => setExpandedTopics(new Set());

  const addTopic = () => {
    setForm(prev => ({ ...prev, topics: [...prev.topics, { title: '', decision: '', category_id: '' }] }));
    // Phase 735.4 — Newly added topic opens automatically (accordion:
    // any previously expanded topic collapses).
    setExpandedTopics(new Set([form.topics.length]));
  };
  const removeTopic = (idx) => {
    setForm(prev => ({ ...prev, topics: prev.topics.filter((_, i) => i !== idx) }));
    // Reindex the expanded set so higher indices shift down after removal.
    setExpandedTopics(prev => {
      const next = new Set();
      prev.forEach(i => {
        if (i === idx) return;
        next.add(i > idx ? i - 1 : i);
      });
      return next;
    });
  };
  const updateTopic = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      topics: prev.topics.map((t, i) => i === idx ? { ...t, [field]: value } : t),
    }));
  };
  const moveTopic = (idx, dir) => {
    setForm(prev => {
      const topics = [...prev.topics];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= topics.length) return prev;
      [topics[idx], topics[newIdx]] = [topics[newIdx], topics[idx]];
      return { ...prev, topics };
    });
    // Swap expansion flags too so an expanded topic stays expanded when
    // it moves up or down.
    setExpandedTopics(prev => {
      const swap = idx + dir;
      if (swap < 0) return prev;
      const next = new Set(prev);
      const hadA = prev.has(idx);
      const hadB = prev.has(swap);
      if (hadA) next.add(swap); else next.delete(swap);
      if (hadB) next.add(idx); else next.delete(idx);
      return next;
    });
  };

  // Phase 733 (revised) — Topics dropdown sources from the existing
  // `agenda_templates` catalogue only. Each topic entry may carry
  // `greek_template_text` / `english_template_text` — when a user picks
  // it in the meeting form, the matching text is auto-inserted into the
  // decision editor as a starting point that they can then edit freely.
  const topicOptions = React.useMemo(() => {
    const seen = new Map();
    (agendaTemplates || []).forEach(tmpl => {
      (tmpl.topics || []).forEach(tp => {
        const key = (tp.title_el || tp.title || '').trim();
        if (!key || seen.has(key)) return;
        seen.set(key, {
          key,
          title_en: key,
          title_el: key,
          category_id: tp.category_id || '',
          greek_text: tp.greek_template_text || '',
          english_text: tp.english_template_text || '',
        });
      });
    });
    return Array.from(seen.values());
  }, [agendaTemplates]);

  // Resolve the dropdown value for a given topic: the matching option key, or
  // "__custom__" if the topic doesn't match any template option.
  const resolveTopicOptionKey = (topic) => {
    if (!topic.title) return '';
    // A topic matches an option when its title equals the option's EN or EL title.
    const match = topicOptions.find(o => o.title_en === topic.title || o.title_el === topic.title);
    return match ? match.key : '__custom__';
  };

  // Apply a selected topic option onto a given topic row.
  const pickTopicOption = (idx, value) => {
    if (value === '__custom__') {
      // Clear title so the user can type; keep existing category/decision.
      setForm(prev => ({
        ...prev,
        topics: prev.topics.map((t, i) => i === idx ? { ...t, title: '', _custom: true } : t),
      }));
      return;
    }
    const opt = topicOptions.find(o => o.key === value);
    if (!opt) return;
    // Phase 733 — Copy the template's Greek/English text into the topic's
    // decision field so the meeting owns its own snapshot. Only inject a
    // template body when the current decision is empty (so editing an
    // existing meeting never overwrites the user's own text).
    const injectedRaw = language === 'el' ? (opt.greek_text || '') : (opt.english_text || opt.greek_text || '');
    // Phase 735.1 — Resolve Agenda-Template variables against the live meeting
    // data BEFORE injecting the text so the decision field never contains
    // literal placeholders like `{chairman_name}`. Variables that can't be
    // resolved (e.g. committee not filled in yet) collapse to empty string and
    // we surface a single toast listing which ones are missing.
    setForm(prev => {
      const vars = buildAgendaVars(prev, apartments, buildingName, companySettings);
      const injected = resolveAgendaVarsInText(injectedRaw, vars);
      const missing = listUnresolvedAgendaVars(injectedRaw, vars);
      if (missing.length && injectedRaw) {
        // One consolidated warning per pick; short list keeps the toast readable.
        toast.warning(
          `Δεν συμπληρώθηκαν ${missing.length} μεταβλητές: ${missing.slice(0, 6).join(', ')}${missing.length > 6 ? '…' : ''}`,
          { duration: 5000 }
        );
      }
      return {
        ...prev,
        topics: prev.topics.map((tp, i) => i === idx ? {
          ...tp,
          title: language === 'el' ? opt.title_el : opt.title_en,
          category_id: opt.category_id || tp.category_id || '',
          decision: (tp.decision && tp.decision.trim())
            ? tp.decision
            : injected,
          _custom: false,
        } : tp),
      };
    });
  };

  const getCategoryName = (catId) => {
    if (!catId) return '';
    const cat = topicCategories.find(c => c.category_id === catId);
    if (!cat) return '';
    return language === 'el' ? (cat.name_el || cat.name) : cat.name;
  };

  // Save
  // Phase 942 — Accept an optional `opts` param so the "Send for Approval"
  // button can save WITHOUT navigating back to the list, then chain into
  // the approval dialog. Also persists `next_meeting_date` / `_time` via
  // the dedicated endpoint so the meeting form is fully self-contained.
  const handleSave = async ({ stayOnForm = false, onDone = null } = {}) => {
    try {
      // Phase 192/207d — Quorum text is always dynamic (never persisted).
      // Legal & committee texts are persisted ONLY when the user has explicitly
      // overridden them per-meeting (non-empty). When empty, we strip them so
      // the central template / auto-generated text stays the source of truth.
      const basePayload = {
        ...form,
        quorum_text: '',
        legal_text: (form.legal_text || '').trim(),
        committee_text: (form.committee_text || '').trim(),
        closing_notes: (form.closing_notes || '').trim(),
        topics: form.topics.filter(t => t.title.trim()),
        committee: form.committee,
      };
      // Phase 942 — Body-save endpoint does NOT accept next_meeting fields
      // (they have a dedicated PUT so the single-active rule can be applied).
      // Strip them from the body and save them separately after.
      const nextDate = (basePayload.next_meeting_date || '').trim();
      const nextTime = (basePayload.next_meeting_time || '').trim();
      delete basePayload.next_meeting_date;
      delete basePayload.next_meeting_time;

      let savedMeeting = null;
      if (editingMeeting) {
        const res = await axios.put(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}`, basePayload);
        savedMeeting = res.data || { ...editingMeeting, ...basePayload };
        setEditingMeeting(savedMeeting);
      } else {
        const res = await axios.post(`${API}/buildings/${buildingId}/meetings`, basePayload);
        savedMeeting = res.data;
        setEditingMeeting(savedMeeting);
      }

      // Phase 942 — Only call the next-meeting endpoint when the meeting is
      // NOT extraordinary (backend rejects it) AND either the date OR time
      // actually changed vs the previously-loaded meeting.
      const isExtraordinary = (savedMeeting?.meeting_type || basePayload.meeting_type) === 'extraordinary';
      const oldDate = (editingMeeting?.next_meeting_date || '').trim();
      const oldTime = (editingMeeting?.next_meeting_time || '').trim();
      const dateChanged = nextDate !== oldDate;
      const timeChanged = nextTime !== oldTime;
      if (!isExtraordinary && savedMeeting?.meeting_id && (dateChanged || timeChanged)) {
        try {
          const body = {};
          if (dateChanged) body.next_meeting_date = nextDate || null;
          // We only send the time key when either the time changed OR the
          // date changed alongside it (the backend clears time when date
          // is cleared, and only overwrites time when the key is present).
          if (timeChanged || dateChanged) body.next_meeting_time = nextTime || null;
          const nres = await axios.put(
            `${API}/buildings/${buildingId}/meetings/${savedMeeting.meeting_id}/next-meeting-date`,
            body,
          );
          savedMeeting = {
            ...savedMeeting,
            next_meeting_date: nres.data?.next_meeting_date ?? null,
            next_meeting_time: nres.data?.next_meeting_time ?? null,
          };
          setEditingMeeting(savedMeeting);
        } catch (err) {
          toast.error(err?.response?.data?.detail || t('errorOccurred'));
        }
      }

      toast.success(editingMeeting ? t('meetingUpdated') : t('meetingCreated'));
      fetchMeetings();
      if (!stayOnForm) {
        setView('list');
      }
      if (onDone) onDone(savedMeeting);
      return savedMeeting;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
      return null;
    }
  };

  // Phase 942 — "Send for Approval" from the meeting form: save the form
  // first (so any pending edits are persisted) then open the approval
  // email composer for the freshly-saved meeting.
  const handleSendForApprovalFromForm = async () => {
    const saved = await handleSave({ stayOnForm: true });
    if (saved) {
      openApprovalDialog(saved);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deletingMeeting) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/meetings/${deletingMeeting.meeting_id}`);
      toast.success(t('meetingDeleted'));
      setDeleteDialogOpen(false);
      setDeletingMeeting(null);
      fetchMeetings();
      if (view !== 'list') setView('list');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  // Refresh financial snapshot
  const refreshSnapshot = async () => {
    try {
      if (editingMeeting) {
        await axios.put(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}`, { refresh_snapshot: true });
        const res = await axios.get(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}`);
        setFinancialSnapshot(res.data.financial_snapshot);
        setEditingMeeting(res.data);
      }
      toast.success(t('savedSuccess'));
    } catch { toast.error(t('errorOccurred')); }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingMeeting) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const res = await axios.get(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}`);
      setEditingMeeting(res.data);
      toast.success(t('savedSuccess'));
    } catch { toast.error(t('errorOccurred')); }
    setUploading(false);
    e.target.value = '';
  };

  const deleteAttachment = async (fileId) => {
    if (!editingMeeting) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}/attachments/${fileId}`);
      const res = await axios.get(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}`);
      setEditingMeeting(res.data);
      toast.success(t('savedSuccess'));
    } catch { toast.error(t('errorOccurred')); }
  };

  // PDF
  // Phase 197 — Show a quick confirmation before exporting so the user is
  // never surprised by what's in the document. The dialog states explicitly
  // whether the Financial Summary will be included or not.
  const confirmExport = (action) => {
    const fsOn = !!editingMeeting?.include_financial_summary;
    const yesNo = fsOn ? 'ΝΑΙ' : 'ΟΧΙ';
    const ok = window.confirm(
      `Συμπερίληψη Οικονομικής Κατάστασης: ${yesNo}\n\n` +
      (fsOn
        ? 'Η Οικονομική Κατάσταση ΘΑ συμπεριληφθεί στο PDF.'
        : 'Η Οικονομική Κατάσταση ΔΕΝ θα εμφανιστεί στο PDF.') +
      '\n\nΣυνέχεια;'
    );
    if (ok) action();
  };
  const handlePrintPdf = (meetingId) => {
    // Phase 735.30 — Open Meeting PDF via the global in-app viewer
    // (see pdfUtils.openPdfInBrowser). Replaces the previous
    // printPdfUtil flow so a single code path serves View, Print and
    // Download from within the modal.
    Promise.resolve(openPdfInBrowserUtil(`/buildings/${buildingId}/meetings/${meetingId}/pdf`, { lang }))
      .catch((err) => {
        console.error('[handlePrintPdf]', err);
        toast.error(err?.message || 'PDF failed');
      });
  };

  // Phase 189 — Next Meeting Date. Only ONE active "next meeting" per building
  // at a time: saving on one meeting automatically clears the field on all
  // other meetings of the same building.
  // Phase 832 — Extended to also carry an optional Next Meeting Time (HH:MM).
  const saveNextMeetingDate = async (meetingId, dateStr, timeStr) => {
    try {
      const body = { next_meeting_date: dateStr || null };
      if (timeStr !== undefined) body.next_meeting_time = timeStr || null;
      await axios.put(
        `${API}/buildings/${buildingId}/meetings/${meetingId}/next-meeting-date`,
        body
      );
      toast.success(t('savedSuccess'));
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  // Phase 735.37 — Mark a meeting as THE Active Management Committee
  // meeting. The backend automatically clears the flag on every other
  // meeting of the same building so only one can be active at a time.
  const setActiveCommitteeMeeting = async (meetingId, active) => {
    try {
      await axios.post(
        `${API}/buildings/${buildingId}/meetings/${meetingId}/set-active-committee`,
        { active }
      );
      toast.success(active
        ? (t('activeCommitteeSet') || 'Marked as Active Management Committee')
        : (t('activeCommitteeUnset') || 'Unmarked as Active Management Committee'));
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openEmailForMeeting = (mtg) => {
    const pdfPath = `/api/buildings/${buildingId}/meetings/${mtg.meeting_id}/pdf?lang=${lang}`;
    const meetingType = t(mtg.meeting_type || 'agm');
    const dateStr = formatDateDMY(mtg.date);
    setEmailProps({
      pdfUrl: pdfPath,
      filename: `Meeting_${buildingName}_${mtg.date}.pdf`,
      defaultEmail: '',
      templateType: 'meeting',
      dynamicFields: {
        building_name: buildingName,
        meeting_date: dateStr,
        meeting_type: meetingType,
      },
    });
    setEmailDialogOpen(true);
  };

  // ==================== LIST VIEW ====================
  if (view === 'list') {
    return (
      <div className="space-y-6" data-testid="meetings-tab">
        <div className="bg-white border border-zinc-200" data-testid="meetings-section">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>{t('meetings')}</h3>
              <p className="text-xs text-zinc-400 mt-0.5">{t('meetingMinutes')}</p>
            </div>
            {isAdmin && (
              <Button onClick={openNewMeeting} size="sm" className="bg-zinc-900 rounded-none" data-testid="new-meeting-btn">
                <Plus size={14} className="mr-1" />{t('newMeeting')}
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            {meetings.length === 0 ? (
              <div className="p-12 text-center text-zinc-400 text-sm" data-testid="no-meetings">{t('noMeetings')}</div>
            ) : (
              <table className="w-full text-sm" data-testid="meetings-table">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/50">
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('meetingDate')}</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('meetingType')}</th>
                    <th className="px-4 py-2.5 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('quorum')}</th>
                    <th className="px-4 py-2.5 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('topicsDecisions')}</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('status')}</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('nextMeeting')}</th>
                    <th className="px-4 py-2.5 w-36 text-xs font-medium text-zinc-500 uppercase tracking-wider">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {meetings.map(mtg => (
                    <tr key={mtg.meeting_id} className="hover:bg-zinc-50/50 transition-colors" data-testid={`meeting-row-${mtg.meeting_id}`}>
                      <td className="px-4 py-2.5 font-mono">{formatDateDMY(mtg.date)}</td>
                      <td className="px-4 py-2.5">{t(mtg.meeting_type || 'agm')}</td>
                      <td className="px-4 py-2.5 text-center font-mono">
                        <span className={`text-xs px-2 py-0.5 border ${mtg.quorum_percentage >= 50 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {(mtg.quorum_percentage || 0).toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center font-mono">{(mtg.topics || []).length}</td>
                      <td className="px-4 py-2.5">
                        <StatusBadge
                          status={mtg.status || 'draft'}
                          onSubmitDraft={isAdmin ? () => openApprovalDialog(mtg) : null}
                        />
                      </td>
                      {/* Phase 189 — Next Meeting Date: inline date picker per meeting.
                          Phase 735.7 — Not applicable to Extraordinary General
                          Meetings (they cannot be re-summoned via a Next
                          Meeting date); the cell is left blank for them. */}
                      {/* Phase 995 — Next Meeting is now available for ALL
                          meeting types, including Extraordinary General
                          Meetings. Previously the cell was rendered as
                          "—" for `extraordinary`; the same fields are
                          now shown so admins can schedule a follow-up
                          when needed. */}
                      <td className="px-4 py-2.5" data-testid={`next-meeting-cell-${mtg.meeting_id}`}>
                        <div className="flex flex-col gap-1">
                            <input
                              type="date"
                              value={mtg.next_meeting_date || ''}
                              onChange={(e) => saveNextMeetingDate(mtg.meeting_id, e.target.value, mtg.next_meeting_time)}
                              disabled={!isAdmin}
                              className="border border-zinc-200 px-2 py-1 text-xs rounded-none font-mono bg-white disabled:bg-zinc-50 disabled:cursor-not-allowed"
                              data-testid={`next-meeting-date-${mtg.meeting_id}`}
                              title={t('nextMeetingHint')}
                            />
                            {/* Phase 832 — Optional Next Meeting Time (HH:MM).
                                Only enabled once a date has been chosen. */}
                            <input
                              type="time"
                              value={mtg.next_meeting_time || ''}
                              onChange={(e) => saveNextMeetingDate(mtg.meeting_id, mtg.next_meeting_date, e.target.value)}
                              disabled={!isAdmin || !mtg.next_meeting_date}
                              className="border border-zinc-200 px-2 py-1 text-xs rounded-none font-mono bg-white disabled:bg-zinc-50 disabled:cursor-not-allowed"
                              data-testid={`next-meeting-time-${mtg.meeting_id}`}
                              placeholder="HH:MM"
                            />
                          </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-1">
                          {/* Phase 735.37 — "Active Committee" star. Ticked
                              (yellow filled star) indicates THIS meeting
                              defines the Management Committee shown across
                              the app. Clicking toggles the flag; the
                              backend auto-clears the flag on other
                              meetings of this building. */}
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => setActiveCommitteeMeeting(mtg.meeting_id, !mtg.is_active_committee_meeting)}
                              className={`p-1.5 transition-colors ${mtg.is_active_committee_meeting ? 'text-amber-500 hover:bg-amber-50' : 'text-zinc-300 hover:bg-amber-50 hover:text-amber-500'}`}
                              title={mtg.is_active_committee_meeting
                                ? (t('activeCommitteeMeetingHint') || 'This meeting defines the active Management Committee — click to unset')
                                : (t('markAsActiveCommittee') || 'Mark as Active Management Committee meeting')}
                              data-testid={`active-committee-${mtg.meeting_id}`}
                            >
                              <Star size={16} weight={mtg.is_active_committee_meeting ? 'fill' : 'regular'} />
                            </button>
                          )}
                          <button onClick={() => openViewMeeting(mtg)} className="p-1.5 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors" title={t('viewMeeting')} data-testid={`view-meeting-${mtg.meeting_id}`}><Eye size={16} /></button>
                          {canEditMeeting(mtg) && <button onClick={() => openEditMeeting(mtg)} className="p-1.5 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors" title={t('editMeeting')} data-testid={`edit-meeting-${mtg.meeting_id}`}><PencilSimple size={16} /></button>}
                          <button onClick={() => handlePrintPdf(mtg.meeting_id)} className="p-1.5 hover:bg-blue-50 text-zinc-500 hover:text-blue-600 transition-colors" title="Open PDF (Greek)" data-testid={`print-meeting-${mtg.meeting_id}`}><Printer size={16} /></button>
                          <button onClick={() => openTranslateDialog(mtg)} className="p-1.5 hover:bg-blue-50 text-zinc-500 hover:text-blue-600 transition-colors" title="English version" data-testid={`translate-meeting-${mtg.meeting_id}`}><Translate size={16} /></button>
                          <button onClick={() => openEmailForMeeting(mtg)} className="p-1.5 hover:bg-purple-50 text-zinc-500 hover:text-purple-600 transition-colors" title={t('sendByEmail')} data-testid={`email-meeting-${mtg.meeting_id}`}><EnvelopeSimple size={16} /></button>
                          {isAdmin && <button onClick={() => { setDeletingMeeting(mtg); setDeleteDialogOpen(true); }} className="p-1.5 hover:bg-zinc-100 text-zinc-500 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-meeting-${mtg.meeting_id}`}><Trash size={16} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Delete confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteMeeting')}</AlertDialogTitle>
              <AlertDialogDescription>{t('deleteMeetingConfirm')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-meeting">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <SendEmailDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} pdfUrl={emailProps.pdfUrl} filename={emailProps.filename} defaultEmail={emailProps.defaultEmail} templateType={emailProps.templateType} dynamicFields={emailProps.dynamicFields} buildingId={buildingId} />
        <MeetingTranslateDialog
          open={translateDialogOpen}
          onOpenChange={setTranslateDialogOpen}
          buildingId={buildingId}
          meetingId={translateMeeting?.meeting_id}
          meetingDate={translateMeeting?.date}
          onSaved={() => fetchMeetings()}
        />
      </div>
    );
  }

  // ==================== DETAIL VIEW ====================
  if (view === 'detail' && editingMeeting) {
    const mtg = editingMeeting;
    const fs = mtg.financial_snapshot || {};
    // Phase 735.1 — Resolve Agenda-Template placeholders at display time using
    // this meeting's own live data. Meetings whose stored `decision` text
    // still contains `{chairman_name}` / `{building_name}` etc. (either from
    // before this fix or from a template snapshot inserted while the
    // committee was incomplete) now render with the actual values.
    const mtgVars = buildAgendaVars(mtg, apartments, buildingName, companySettings);
    return (
      <>
      <div className="space-y-6" data-testid="meeting-detail">
        <div className="flex items-center justify-between">
          <button onClick={() => setView('list')} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="back-to-list">
            <ArrowLeft size={16} />{t('backToList')}
          </button>
          <div className="flex gap-2">
            {canEditMeeting(mtg) && <Button onClick={() => openEditMeeting(mtg)} size="sm" variant="outline" className="rounded-none" data-testid="edit-from-detail"><PencilSimple size={14} className="mr-1" />{t('editMeeting')}</Button>}
            <Button onClick={() => confirmExport(() => handlePrintPdf(mtg.meeting_id))} size="sm" variant="outline" className="rounded-none" data-testid="print-from-detail"><Printer size={14} className="mr-1" />Open PDF (Greek)</Button>
            <Button onClick={() => openTranslateDialog(mtg)} size="sm" variant="outline" className="rounded-none border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="translate-from-detail"><Translate size={14} className="mr-1" />English version</Button>
            <Button onClick={() => openEmailForMeeting(mtg)} size="sm" className="bg-zinc-900 rounded-none" data-testid="email-from-detail"><EnvelopeSimple size={14} className="mr-1" />{t('sendByEmail')}</Button>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white border border-zinc-200 p-5">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t(mtg.meeting_type || 'agm')}</h2>
          <p className="text-sm text-zinc-500 mt-1">{mtg.building_name} — {formatDateDMY(mtg.date)} {mtg.time && `| ${mtg.time}`} {mtg.location && `| ${mtg.location}`}</p>
          <div className="mt-2">
            <StatusBadge status={mtg.status || 'draft'} />
          </div>
        </div>

        {/* Phase 207 — Workflow Action Panel (Approval / Signature / Send) */}
        <div className="space-y-2" data-testid="owner-sync-detail-wrap">
          <MeetingOwnerSync
            key={`${mtg.meeting_id}-${(mtg.owner_sync_log || []).length}`}
            buildingId={buildingId}
            meetingId={mtg.meeting_id}
            onSynced={async () => {
              const fresh = await axios.get(`${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}`);
              setEditingMeeting(fresh.data);
              fetchMeetings();
            }}
          />
        </div>
        <WorkflowPanel
          mtg={mtg}
          buildingId={buildingId}
          isAdmin={isAdmin}
          onSubmitDraftClick={() => openApprovalDialog(mtg)}
          onChanged={async () => {
            const fresh = await axios.get(`${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}`);
            setEditingMeeting(fresh.data);
            fetchMeetings();
          }}
        />

        {/* Attendance */}
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('attendance')}</h3>
            <span className={`text-xs font-mono px-2 py-0.5 border ${(mtg.quorum_percentage || 0) >= 50 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
              {t('quorum')}: {(mtg.quorum_percentage || 0).toFixed(2)}%
            </span>
          </div>
          {/* Attendance Summary */}
          {(() => {
            const att = mtg.attendance || [];
            const pres = att.filter(a => a.present);
            const abs = att.filter(a => !a.present);
            const total = att.length;
            // Phase 735.2 — The summary shows ONLY the Quorum Percentage
            // (ownership-share based, sourced from `mtg.quorum_percentage`
            // written by the backend). Attendance-count percentages were
            // intentionally removed so a single value ("Απαρτία") appears
            // consistently across Overview / Attendance section /
            // auto-generated texts / PDF.
            const quorumPct = (mtg.quorum_percentage || 0).toFixed(2);
            return (
              <div className="grid grid-cols-4 gap-3 px-5 py-3 bg-zinc-50/50 border-b border-zinc-100" data-testid="detail-attendance-summary">
                <div className="text-center"><p className="text-[10px] text-zinc-400 uppercase">{t('totalParticipants')}</p><p className="font-mono font-bold text-lg">{total}</p></div>
                <div className="text-center"><p className="text-[10px] text-emerald-600 uppercase">{t('totalPresent')}</p><p className="font-mono font-bold text-lg text-emerald-700">{pres.length}</p></div>
                <div className="text-center"><p className="text-[10px] text-red-500 uppercase">{t('totalAbsent')}</p><p className="font-mono font-bold text-lg text-red-600">{abs.length}</p></div>
                <div className="text-center"><p className="text-[10px] text-zinc-400 uppercase">{t('quorum')}</p><p className={`font-mono font-bold text-lg ${(mtg.quorum_percentage || 0) >= 50 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="detail-quorum-percentage">{quorumPct}%</p></div>
              </div>
            );
          })()}
          {/* Present list */}
          <div className="px-5 py-2 border-b border-zinc-100">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">✓ {t('present')} ({(mtg.attendance || []).filter(a => a.present).length})</p>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-zinc-100">
              {(mtg.attendance || []).filter(a => a.present).map(att => {
                const proxy = typeof att.represented_by === 'string' ? att.represented_by.trim() : '';
                return (
                  <tr key={att.apartment_id}>
                    <td className="px-4 py-2 font-semibold w-20 align-top">{att.apartment_number}</td>
                    <td className="px-4 py-2 text-zinc-600">
                      <div>{att.owner_name || '-'}</div>
                      {proxy && (
                        <div className="text-[11px] text-amber-700 italic mt-0.5">(Εκπροσωπείται από: {proxy})</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-zinc-500 w-24 align-top">{(att.share_percentage || 0).toFixed(2)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Absent list */}
          <div className="px-5 py-2 border-b border-t border-zinc-100">
            <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">✗ {t('absent')} ({(mtg.attendance || []).filter(a => !a.present).length})</p>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-zinc-100">
              {(mtg.attendance || []).filter(a => !a.present).map(att => (
                <tr key={att.apartment_id}>
                  <td className="px-4 py-2 font-semibold w-20">{att.apartment_number}</td>
                  <td className="px-4 py-2 text-zinc-600">{att.owner_name || '-'}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-500 w-24">{(att.share_percentage || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Quorum & Legal Text */}
        {(mtg.quorum_text || mtg.legal_text) && (
          <div className="bg-white border border-zinc-200 p-5 space-y-3">
            {mtg.quorum_text && <p className="text-sm text-zinc-700 leading-relaxed">{mtg.quorum_text}</p>}
            {mtg.legal_text && <p className="text-xs text-zinc-500 italic leading-relaxed">{mtg.legal_text}</p>}
          </div>
        )}

        {/* Management Committee
            Phase 735.5 — Hidden for Extraordinary General Meetings even
            when the meeting doc still carries committee members (data
            preserved on the database but never rendered while the type is
            "extraordinary"). */}
        {(mtg.committee || []).length > 0 && (mtg.meeting_type || '').toLowerCase() !== 'extraordinary' && (
          <div className="bg-white border border-zinc-200" data-testid="detail-committee-section">
            <div className="px-5 py-3 border-b border-zinc-100">
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('managementCommittee')}</h3>
            </div>
            {mtg.committee_text && <p className="px-5 py-3 text-sm text-zinc-700 border-b border-zinc-100">{mtg.committee_text}</p>}
            <div className="divide-y divide-zinc-100">
              {mtg.committee.filter(m => m.apartment_id || m.custom_name).map((member, i) => {
                const apt = (apartments || []).find(a => a.apartment_id === member.apartment_id);
                const name = member.apartment_id && apt ? apt.owner_name || '-' : member.custom_name || '-';
                const aptLabel = member.apartment_id && apt ? ` (${t('apartmentShort')} ${apt.apartment_number})` : '';
                return (
                  <div key={i} className="px-5 py-3 flex items-center gap-4" data-testid={`detail-committee-${member.role || i}`}>
                    <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wide w-24">{member.role ? t(member.role) : '-'}</span>
                    <span className="text-sm font-medium">{name}{aptLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Topics & Decisions */}
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-100">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('topicsDecisions')}</h3>
          </div>
          <div className="divide-y divide-zinc-100">
            {(mtg.topics || []).length === 0 ? (
              <div className="p-6 text-center text-zinc-400 text-sm">{t('noTopics')}</div>
            ) : (
              (mtg.topics || []).map((topic, i) => (
                <div key={i} className="px-5 py-4">
                  <p className="font-semibold text-sm">{i + 1}. {topic.title}
                    {topic.category_id && (() => {
                      const cat = topicCategories.find(c => c.category_id === topic.category_id);
                      const catLabel = cat ? (language === 'el' ? (cat.name_el || cat.name) : cat.name) : '';
                      return catLabel ? <span className="text-xs text-zinc-400 font-normal ml-2">[{catLabel}]</span> : null;
                    })()}
                  </p>
                  {topic.decision && <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap pl-4 border-l-2 border-zinc-200">{resolveAgendaVarsInText(topic.decision, mtgVars)}</p>}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Financial Snapshot */}
        {fs && Object.keys(fs).length > 0 && (
          <div className="bg-white border border-zinc-200">
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('financialSnapshot')}</h3>
              {fs.captured_at && <span className="text-[10px] text-zinc-400">{t('snapshotCapturedAt')} {formatDateTimeDMY(fs.captured_at)}</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-100">
              {[
                [t('totalExpectedIncome'), fs.total_expected],
                [t('totalCollected'), fs.total_collected],
                [t('totalOutstanding'), fs.total_outstanding],
                [t('totalExpenses'), fs.total_expenses],
                [t('reserveFund'), fs.reserve_fund_balance],
                [t('cashBalance'), fs.cash_balance],
                [t('bankBalance'), fs.bank_balance],
                [t('repairsTotal'), fs.repairs_total],
              ].map(([label, val], idx) => (
                <div key={idx} className="bg-white p-4">
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wide">{label}</p>
                  <p className="text-base font-bold font-mono mt-1">&euro;{(val || 0).toFixed(2)}</p>
                </div>
              ))}
            </div>
            {/* Repairs breakdown */}
            {(fs.repairs || []).length > 0 && (
              <div className="px-5 py-3 border-t border-zinc-100">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">{t('repairs')}</p>
                {fs.repairs.map((rep, ri) => (
                  <RepairDetail key={ri} repair={rep} t={t} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Attachments */}
        {(mtg.attachments || []).length > 0 && (
          <div className="bg-white border border-zinc-200">
            <div className="px-5 py-3 border-b border-zinc-100">
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('attachments')}</h3>
            </div>
            <div className="divide-y divide-zinc-100">
              {mtg.attachments.map(att => (
                <div key={att.file_id} className="px-5 py-2.5 flex items-center justify-between text-sm">
                  <a href={`${API}/buildings/${buildingId}/meetings/${mtg.meeting_id}/attachments/${att.file_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900">
                    <Paperclip size={14} />{att.filename}
                  </a>
                  <span className="text-xs text-zinc-400">{att.uploaded_at ? formatDateDMYUtil(att.uploaded_at) : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <SendEmailDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} pdfUrl={emailProps.pdfUrl} filename={emailProps.filename} defaultEmail={emailProps.defaultEmail} templateType={emailProps.templateType} dynamicFields={emailProps.dynamicFields} buildingId={buildingId} />
      <MeetingTemplatesDialog open={templatesDialogOpen} onOpenChange={setTemplatesDialogOpen} />
      <ApprovalEmailDialog
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        buildingId={buildingId}
        meeting={approvalDialogMeeting}
        onSubmitted={() => {
          fetchMeetings();
          if (editingMeeting && approvalDialogMeeting?.meeting_id === editingMeeting.meeting_id) {
            axios.get(`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}`)
              .then(r => setEditingMeeting(r.data))
              .catch(() => {});
          }
        }}
      />
      <MeetingTranslateDialog
        open={translateDialogOpen}
        onOpenChange={setTranslateDialogOpen}
        buildingId={buildingId}
        meetingId={translateMeeting?.meeting_id}
        meetingDate={translateMeeting?.date}
        onSaved={() => fetchMeetings()}
      />
      </>
    );
  }

  // ==================== FORM VIEW ====================
  return (
    <div className="space-y-6" data-testid="meeting-form">
      <div className="flex items-center justify-between">
        <button onClick={() => setView('list')} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="form-back-to-list">
          <ArrowLeft size={16} />{t('backToList')}
        </button>
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{editingMeeting ? t('editMeeting') : t('newMeeting')}</h2>
          {/* Phase 942 — Surface the workflow status right in the form
              header so admins know at a glance whether "Send for Approval"
              is available. */}
          {editingMeeting && (
            <StatusBadge status={editingMeeting.status || 'draft'} />
          )}
        </div>
      </div>

      {/* 1. General Information */}
      {editingMeeting && (
        <div className="space-y-2" data-testid="owner-sync-form-wrap">
          <MeetingOwnerSync
            buildingId={buildingId}
            meetingId={editingMeeting.meeting_id}
            onSynced={(applied) => setForm((prev) => ({
              ...prev,
              attendance: prev.attendance.map((a) => applied.filter((c) => c.apartment_id === a.apartment_id)
                .reduce((acc, c) => ({ ...acc, [c.field]: c.new }), a)),
            }))}
          />
        </div>
      )}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-100">
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('generalInfo')}</h3>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div>
            <Label>{t('meetingType')}</Label>
            <select value={form.meeting_type} onChange={(e) => setForm({ ...form, meeting_type: e.target.value })} className="mt-1 w-full border border-zinc-200 px-3 py-2 text-sm rounded-none font-mono bg-white" data-testid="meeting-type-select">
              {MEETING_TYPES.map(mt => <option key={mt} value={mt}>{t(mt)}</option>)}
            </select>
          </div>
          <div>
            <Label>{t('meetingDate')}</Label>
            <DatePickerDMY value={form.date} onChange={v => setForm({ ...form, date: v })} className="mt-1 font-mono" data-testid="meeting-date" />
          </div>
          <div>
            <Label>{t('meetingTime')}</Label>
            <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="meeting-time" />
          </div>
          <div>
            <Label>{t('endTime')}</Label>
            <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="meeting-end-time" />
          </div>
          <div>
            <Label>{t('meetingLocation')}</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 rounded-none" placeholder={buildingName} data-testid="meeting-location" />
          </div>
          {form.time && form.end_time && (
            <div>
              <Label>{t('duration')}</Label>
              <p className="mt-2 text-sm font-mono text-zinc-700" data-testid="meeting-duration">{computeDuration(form.time, form.end_time)}</p>
            </div>
          )}
          {/* Phase 995 — Next Meeting Date + Time available for ALL
              meeting types (including Extraordinary General Meetings).
              Backed by the dedicated `/next-meeting-date` endpoint
              (called inside handleSave) so single-active rule +
              validation still apply. */}
          <>
              <div>
                <Label>
                  {t('nextMeetingDate') || 'Επόμενη Ημ/νία Συνέλευσης'}
                  <span className="ml-1 text-[10px] text-zinc-400 font-normal">
                    ({t('optional') || 'προαιρετικό'})
                  </span>
                </Label>
                <DatePickerDMY
                  value={form.next_meeting_date}
                  onChange={(v) => setForm({ ...form, next_meeting_date: v })}
                  className="mt-1 font-mono"
                  data-testid="meeting-next-date"
                />
                <p className="text-[10px] text-zinc-400 mt-1 italic">
                  {t('nextMeetingHint') || 'Χρησιμοποιείται αυτόματα όταν δεν υπάρχει απαρτία. Εμφανίζεται στο Ημερολόγιο.'}
                </p>
              </div>
              <div>
                <Label>
                  {t('nextMeetingTime') || 'Επόμενη Ώρα Συνέλευσης'}
                  <span className="ml-1 text-[10px] text-zinc-400 font-normal">
                    ({t('optional') || 'προαιρετικό'})
                  </span>
                </Label>
                <Input
                  type="time"
                  value={form.next_meeting_time}
                  onChange={(e) => setForm({ ...form, next_meeting_time: e.target.value })}
                  disabled={!form.next_meeting_date}
                  className="mt-1 rounded-none font-mono disabled:bg-zinc-50 disabled:cursor-not-allowed"
                  data-testid="meeting-next-time"
                  placeholder="HH:MM"
                />
              </div>
          </>
          {/* Phase 173/197 — Financial Summary opt-in moved out of "General
              Information" into a dedicated PDF Options panel near the Save
              action so it's not buried. See bottom of the form. */}
        </div>
      </div>

      {/* 2. Attendance */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('attendance')}</h3>
          <span className={`text-xs font-mono px-2 py-0.5 border ${quorum >= 50 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="quorum-display">
            {t('quorum')}: {quorum.toFixed(2)}%
          </span>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-200 bg-zinc-50/50">
            <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase">{t('apartmentNumber')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase">{t('ownerName')}</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-zinc-500 uppercase">{t('sharePercentage')}</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-zinc-500 uppercase">{t('presentAbsent')}</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-100">
            {form.attendance.map(att => {
              // Phase 190 — Tri-state detection uses typeof check so an EMPTY
              // `represented_by` string still identifies the row as "represented"
              // (fixes the bug where selecting "Εκπροσωπείται" instantly flipped
              // back to "Παρών" because the placeholder space was trimmed away).
              // Convention:
              //   absent      → present: false, represented_by: null/undefined
              //   present     → present: true,  represented_by: null/undefined
              //   represented → present: true,  represented_by: '' or 'Some Name'
              const isRepresented = typeof att.represented_by === 'string';
              const state = isRepresented ? 'represented' : (att.present ? 'present' : 'absent');
              const setState = (newState) => {
                setForm(prev => ({
                  ...prev,
                  attendance: prev.attendance.map(a => {
                    if (a.apartment_id !== att.apartment_id) return a;
                    if (newState === 'present') {
                      const { represented_by: _rb, ...rest } = a;
                      return { ...rest, present: true };
                    }
                    if (newState === 'absent') {
                      const { represented_by: _rb, ...rest } = a;
                      return { ...rest, present: false };
                    }
                    // represented → present=true + empty string (preserve existing if any)
                    return { ...a, present: true, represented_by: typeof a.represented_by === 'string' ? a.represented_by : '' };
                  }),
                }));
              };
              const setRepBy = (val) => setForm(prev => ({
                ...prev,
                attendance: prev.attendance.map(a => a.apartment_id === att.apartment_id ? { ...a, represented_by: val } : a),
              }));
              // Phase 190 — Dropdown of same-building owners (excluding self)
              // so the user picks a real apartment owner as the proxy.
              const proxyOptions = (apartments || [])
                .filter(o => o.apartment_id !== att.apartment_id && (o.owner_name || '').trim())
                .sort((a, b) => String(a.apartment_number).localeCompare(String(b.apartment_number), undefined, { numeric: true }));
              return (
                <tr key={att.apartment_id} className={att.present ? 'bg-emerald-50/30' : ''} data-testid={`att-row-${att.apartment_id}`}>
                  <td className="px-4 py-2 font-semibold">{att.apartment_number}</td>
                  <td className="px-4 py-2 text-zinc-500">{att.owner_name || '-'}</td>
                  <td className="px-4 py-2 text-center font-mono">{(att.share_percentage || 0).toFixed(2)}%</td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <select
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className={`border px-2 py-1 text-xs rounded-none bg-white ${state === 'present' ? 'border-emerald-300 text-emerald-700' : state === 'represented' ? 'border-amber-300 text-amber-700' : 'border-zinc-300 text-zinc-500'}`}
                        data-testid={`att-state-${att.apartment_id}`}
                      >
                        <option value="present">{t('present')}</option>
                        <option value="absent">{t('absent')}</option>
                        <option value="represented">{t('represented')}</option>
                      </select>
                      {state === 'represented' && (
                        <div className="flex items-center gap-1">
                          <select
                            value={att.represented_by || ''}
                            onChange={(e) => setRepBy(e.target.value)}
                            className="border border-amber-300 px-2 py-1 text-xs rounded-none bg-white min-w-[200px]"
                            data-testid={`att-repby-select-${att.apartment_id}`}
                          >
                            <option value="">{t('representedByPlaceholder') || '— Επιλέξτε Εκπρόσωπο —'}</option>
                            {proxyOptions.map(o => (
                              <option key={o.apartment_id} value={o.owner_name}>
                                {o.owner_name} (Διαμ. {o.apartment_number})
                              </option>
                            ))}
                          </select>
                          <Input
                            value={att.represented_by || ''}
                            onChange={(e) => setRepBy(e.target.value)}
                            className="w-36 rounded-none text-xs h-7"
                            placeholder="ή ελεύθερο κείμενο"
                            data-testid={`att-repby-${att.apartment_id}`}
                            title="Ή πληκτρολογήστε εκπρόσωπο εκτός πολυκατοικίας"
                          />
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-zinc-100">
          {/* Attendance Summary
              Phase 735.2 — Removed per-card attendance-count percentages so
              the ONLY percentage displayed is the (ownership-based) Quorum.
              Keeps Overview / Attendance / Auto-texts / PDF in agreement. */}
          <div className="grid grid-cols-4 gap-3 mb-3" data-testid="attendance-summary">
            <div className="text-center p-2 bg-zinc-50 border border-zinc-200">
              <p className="text-[10px] text-zinc-400 uppercase">{t('totalParticipants')}</p>
              <p className="font-mono font-bold text-lg">{totalApts}</p>
            </div>
            <div className="text-center p-2 bg-emerald-50 border border-emerald-200">
              <p className="text-[10px] text-emerald-600 uppercase">{t('totalPresent')}</p>
              <p className="font-mono font-bold text-lg text-emerald-700">{presentCount}</p>
            </div>
            <div className="text-center p-2 bg-red-50 border border-red-200">
              <p className="text-[10px] text-red-500 uppercase">{t('totalAbsent')}</p>
              <p className="font-mono font-bold text-lg text-red-600">{absentCount}</p>
            </div>
            <div className="text-center p-2 bg-zinc-50 border border-zinc-200">
              <p className="text-[10px] text-zinc-400 uppercase">{t('quorum')}</p>
              <p className={`font-mono font-bold text-lg ${quorum >= 50 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="form-quorum-percentage">{quorum.toFixed(2)}%</p>
              <p className="text-[10px] text-zinc-400">{quorum >= 50 ? t('quorumReached') : t('quorumNotReached')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 2b. Auto-Generated Texts (READ-ONLY PREVIEW — Phase 192)
          - Quorum paragraph: 100% dynamic, auto only, read-only
          - Legal & Committee: managed centrally via Settings → Meeting Text Templates
      */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('autoGeneratedTexts')}</h3>
            <p className="text-[11px] text-zinc-400 mt-0.5">Προεπισκόπηση — τα κείμενα εφαρμόζονται αυτόματα στο PDF</p>
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setTemplatesDialogOpen(true)}
              className="text-[11px] font-semibold text-zinc-700 hover:text-zinc-900 flex items-center gap-1 border border-zinc-300 px-2.5 py-1.5 hover:bg-zinc-50 transition-colors"
              data-testid="edit-meeting-templates-link"
              title="Επεξεργασία κεντρικών προτύπων (Νομικό & Επιτροπή)"
            >
              <PencilSimple size={12} /> Επεξεργασία Αυτόματων Κειμένων
            </button>
          )}
        </div>
        <div className="p-5 space-y-4">
          {/* Phase 194 — No-quorum warning: require the admin to set the next
              meeting date before the PDF can be exported.
              Phase 735.7 — Skip both warnings for Extraordinary General
              Meetings — Next Meeting has no meaning for that type.
              Phase 942 — Compare against the live form value (users can
              now edit the date directly on the meeting form). */}
          {form.meeting_type !== 'extraordinary' && quorum < 50 && !(form.next_meeting_date || editingMeeting?.next_meeting_date) && (
            <div className="border-l-4 border-red-500 bg-red-50 px-3 py-2.5 text-xs text-red-800" data-testid="no-quorum-date-warning">
              <p className="font-semibold mb-0.5">Δεν υπάρχει απαρτία (&lt;50%).</p>
              <p className="leading-snug">
                Πριν την εξαγωγή του PDF πρέπει να οριστεί η ημερομηνία νέας συνέλευσης — μπορείτε να τη συμπληρώσετε στο πεδίο «Επόμενη Ημ/νία Συνέλευσης» στη Γενική Πληροφορία παραπάνω.
              </p>
            </div>
          )}
          {form.meeting_type !== 'extraordinary' && quorum < 50 && (form.next_meeting_date || editingMeeting?.next_meeting_date) && (
            <div className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2.5 text-xs text-amber-900" data-testid="no-quorum-reduced-doc">
              Λόγω έλλειψης απαρτίας, το PDF θα περιλαμβάνει μόνο το παρουσιολόγιο και την παράγραφο απαρτίας. Δεν θα εκτυπωθούν Διαχειριστική Επιτροπή, Θέματα &amp; Αποφάσεις, ή Οικονομική Κατάσταση.
            </div>
          )}

          {/* Quorum paragraph — DYNAMIC, READ-ONLY */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold text-zinc-700">{t('quorumDurationText')}</Label>
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 uppercase font-semibold">
                Αυτόματο (βάσει απαρτίας)
              </span>
            </div>
            <div
              className={`border px-3 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${quorum >= 50 ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40'}`}
              data-testid="quorum-text-preview"
            >
              {generateQuorumText()}
            </div>
            <p className="text-[10px] text-zinc-400 mt-1 italic">
              Δεν είναι επεξεργάσιμο. Αλλάζει δυναμικά όταν η απαρτία περάσει το 50%.
            </p>
          </div>

          {/* Legal Quorum Text — per-meeting override (Phase 207d/207f) */}
          <EditableAutoText
            label={t('legalQuorumText')}
            autoText={meetingTemplates.legal_text || generateLegalText()}
            value={form.legal_text}
            savedValue={editingMeeting?.legal_text || ''}
            onChange={(v) => setForm({ ...form, legal_text: v })}
            testIdBase="legal-text"
          />

          {/* Committee Decision Text — per-meeting override (Phase 207d/207f).
              Phase 735.5 — Extraordinary General Meetings do NOT elect a
              Management Committee, so this block is hidden for that type.
              The saved `form.committee_text` value is preserved in state and
              on the meeting document so switching back to AGM restores it. */}
          {form.meeting_type !== 'extraordinary' && (
            <EditableAutoText
              label={t('committeeDecisionText')}
              autoText={meetingTemplates.committee_text || generateCommitteeText()}
              value={form.committee_text}
              savedValue={editingMeeting?.committee_text || ''}
              onChange={(v) => setForm({ ...form, committee_text: v })}
              testIdBase="committee-text"
            />
          )}

          {/* Phase 209c/209e/209g — Epilogue (single editable closing block). */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>
              Επίλογος / Epilogue
              <span className="ml-2 text-[10px] font-normal text-zinc-400 normal-case">You can edit the epilogue text if needed</span>
            </label>
            <textarea
              value={form.closing_notes}
              onChange={(e) => setForm({ ...form, closing_notes: e.target.value })}
              rows={4}
              placeholder="π.χ. Η συνεδρίαση ολοκληρώθηκε στις 20:30. Όλες οι αποφάσεις πάρθηκαν με πλειοψηφία."
              className="w-full text-sm border border-zinc-300 px-3 py-2 rounded-none focus:border-zinc-900 focus:outline-none"
              data-testid="closing-notes-input"
            />
            {defaultClosingNotes && form.closing_notes !== defaultClosingNotes && (
              <button
                type="button"
                onClick={() => setForm({ ...form, closing_notes: defaultClosingNotes })}
                className="text-[10px] text-blue-600 hover:underline"
                data-testid="reset-closing-notes-default"
              >
                ↺ Επαναφορά στο default κείμενο
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 3. Management Committee
          Phase 735.5 — Only shown for meeting types that actually elect /
          appoint a Management Committee. Extraordinary General Meetings
          skip this section entirely; the underlying `form.committee` data
          is preserved and re-appears when the type is switched back. */}
      {form.meeting_type !== 'extraordinary' && (
      <div className="bg-white border border-zinc-200" data-testid="committee-section">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('managementCommittee')}</h3>
          <Button onClick={addCommitteeMember} size="sm" variant="outline" className="rounded-none" data-testid="add-committee-btn">
            <Plus size={14} className="mr-1" />{t('addMember')}
          </Button>
        </div>
        <div className="divide-y divide-zinc-100">
          {(form.committee || []).map((member, idx) => {
            const isPreset = COMMITTEE_ROLES.includes(member.role);
            return (
              <div key={idx} className="p-5 flex items-start gap-4" data-testid={`committee-row-${idx}`}>
                {/* Role */}
                <div className="w-40 flex-shrink-0">
                  <Label className="text-[10px] text-zinc-400 uppercase">{t('role')}</Label>
                  {isPreset ? (
                    <p className="mt-1 text-sm font-semibold">{t(member.role)}</p>
                  ) : (
                    <Input
                      value={member.role}
                      onChange={(e) => updateCommitteeMember(idx, 'role', e.target.value)}
                      className="mt-1 rounded-none text-sm"
                      placeholder={t('role')}
                      data-testid={`committee-role-${idx}`}
                    />
                  )}
                </div>
                {/* Person: dropdown or custom */}
                <div className="flex-1">
                  <Label className="text-[10px] text-zinc-400 uppercase">{t('selectOwnerOrCustom')}</Label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={member.apartment_id || ''}
                      onChange={(e) => updateCommitteeMember(idx, 'apartment_id', e.target.value)}
                      className="border border-zinc-200 px-3 py-2 text-sm rounded-none bg-white flex-1"
                      data-testid={`committee-apt-${idx}`}
                    >
                      <option value="">{t('selectFromApartments')}</option>
                      {(apartments || []).map(a => (
                        <option key={a.apartment_id} value={a.apartment_id}>
                          {a.owner_name || '-'} ({t('apartmentShort')} {a.apartment_number})
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-zinc-400 self-center">{t('or')}</span>
                    <Input
                      value={member.custom_name || ''}
                      onChange={(e) => updateCommitteeMember(idx, 'custom_name', e.target.value)}
                      className="rounded-none text-sm flex-1"
                      placeholder={t('enterNameManually')}
                      disabled={!!member.apartment_id}
                      data-testid={`committee-custom-${idx}`}
                    />
                  </div>
                  {/* Show selected display */}
                  {(member.apartment_id || member.custom_name) && (
                    <p className="mt-1.5 text-xs text-zinc-500" data-testid={`committee-display-${idx}`}>
                      {t(member.role || 'member')}: <span className="font-semibold text-zinc-700">{getCommitteeDisplay(member)}</span>
                    </p>
                  )}
                </div>
                {/* Phase 207j — Active toggle. Drives the live "Committee" panel
                     in Building Overview (only active=true members appear). */}
                <div className="flex flex-col items-center gap-1 pt-5 w-20">
                  <Label className="text-[10px] text-zinc-400 uppercase">Active</Label>
                  <label className="relative inline-flex items-center cursor-pointer" data-testid={`committee-active-${idx}`}>
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={member.active !== false}
                      onChange={(e) => updateCommitteeMember(idx, 'active', e.target.checked)}
                    />
                    <div className="w-9 h-5 bg-zinc-300 peer-checked:bg-emerald-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4"></div>
                  </label>
                </div>
                {/* Remove (only for non-preset extra members) */}
                {!isPreset && (
                  <button onClick={() => removeCommitteeMember(idx)} className="p-1.5 hover:bg-zinc-100 text-zinc-400 hover:text-red-600 mt-5" data-testid={`remove-committee-${idx}`}>
                    <Trash size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* 4. Topics & Decisions
          Phase 735.4 — Collapsible accordion (only one topic open at a time,
          Expand All / Collapse All override), Category dropdown removed. */}
      <div className="bg-white border border-zinc-200" data-testid="topics-decisions-section">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('topicsDecisions')}</h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={expandAllTopics}
              className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 border border-zinc-300 px-2.5 py-1.5 hover:bg-zinc-50 transition-colors rounded-none"
              data-testid="topics-expand-all"
              title={t('expandAll') || 'Expand All'}
            >
              {t('expandAll') || 'Ανάπτυξη Όλων'}
            </button>
            <button
              type="button"
              onClick={collapseAllTopics}
              className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 border border-zinc-300 px-2.5 py-1.5 hover:bg-zinc-50 transition-colors rounded-none"
              data-testid="topics-collapse-all"
              title={t('collapseAll') || 'Collapse All'}
            >
              {t('collapseAll') || 'Σύμπτυξη Όλων'}
            </button>
            <Button onClick={addTopic} size="sm" variant="outline" className="rounded-none" data-testid="add-topic-btn">
              <Plus size={14} className="mr-1" />{t('addTopic')}
            </Button>
          </div>
        </div>
        <div className="divide-y divide-zinc-100">
          {form.topics.map((topic, idx) => {
            const optKey = resolveTopicOptionKey(topic);
            const isCustom = topic._custom || optKey === '__custom__';
            const isOpen = expandedTopics.has(idx);
            const displayTitle = (topic.title || '').trim() || t('untitledTopic') || 'Χωρίς Τίτλο';
            return (
              <div key={idx} data-testid={`topic-${idx}`}>
                {/* Header — always visible; click to expand/collapse.
                    Reorder/remove buttons live inside the header so they
                    stay reachable without expanding the row. */}
                <div className="flex items-center gap-2 px-5 py-3 hover:bg-zinc-50/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleTopicExpanded(idx)}
                    className="flex-1 flex items-center gap-2 text-left"
                    data-testid={`topic-toggle-${idx}`}
                    aria-expanded={isOpen}
                  >
                    {isOpen
                      ? <CaretDown size={14} className="text-zinc-500 flex-shrink-0" />
                      : <CaretUp size={14} className="text-zinc-500 flex-shrink-0 rotate-180" />}
                    <span className="text-xs font-bold text-zinc-400 w-6 flex-shrink-0">{idx + 1}.</span>
                    <span className={`text-sm font-semibold truncate ${(topic.title || '').trim() ? 'text-zinc-900' : 'text-zinc-400 italic'}`}>
                      {displayTitle}
                    </span>
                  </button>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button type="button" onClick={() => moveTopic(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-zinc-200 disabled:opacity-30 transition-colors" data-testid={`topic-up-${idx}`}><ArrowUp size={14} /></button>
                    <button type="button" onClick={() => moveTopic(idx, 1)} disabled={idx === form.topics.length - 1} className="p-1 hover:bg-zinc-200 disabled:opacity-30 transition-colors" data-testid={`topic-down-${idx}`}><ArrowDown size={14} /></button>
                    {form.topics.length > 1 && (
                      <button type="button" onClick={() => removeTopic(idx)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-red-600" data-testid={`remove-topic-${idx}`}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Body — only mounted when expanded. Collapsing does NOT
                    touch topic state, so the decision text is fully
                    preserved. */}
                {isOpen && (
                  <div className="px-5 pb-5 pt-1 space-y-2" data-testid={`topic-body-${idx}`}>
                    <div className="flex gap-2 pl-8">
                      <select
                        value={isCustom ? '__custom__' : optKey}
                        onChange={(e) => pickTopicOption(idx, e.target.value)}
                        className="border border-zinc-200 px-3 py-2 text-sm rounded-none bg-white font-semibold flex-1 min-w-0"
                        data-testid={`topic-option-${idx}`}
                      >
                        <option value="">{t('selectTopic')}</option>
                        {topicOptions.map(o => (
                          <option key={o.key} value={o.key}>
                            {language === 'el' ? o.title_el : o.title_en}
                          </option>
                        ))}
                        <option value="__custom__">{t('customTopic')}</option>
                      </select>
                      {isCustom && (
                        <Input
                          value={topic.title}
                          onChange={(e) => updateTopic(idx, 'title', e.target.value)}
                          className="rounded-none flex-1"
                          placeholder={t('topicPlaceholder')}
                          data-testid={`topic-title-${idx}`}
                        />
                      )}
                    </div>
                    <div className="pl-8">
                      <Textarea
                        value={topic.decision}
                        onChange={(e) => updateTopic(idx, 'decision', e.target.value)}
                        className="rounded-none text-sm"
                        placeholder={t('decisionPlaceholder')}
                        rows={4}
                        data-testid={`topic-decision-${idx}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 4. Financial Snapshot (shown after first save or for existing meetings) */}
      {financialSnapshot && (
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('financialSnapshot')}</h3>
            <div className="flex items-center gap-2">
              {financialSnapshot.captured_at && <span className="text-[10px] text-zinc-400">{t('snapshotCapturedAt')} {formatDateTimeDMY(financialSnapshot.captured_at)}</span>}
              {editingMeeting && (
                <Button onClick={refreshSnapshot} size="sm" variant="outline" className="rounded-none text-xs" data-testid="refresh-snapshot-btn">
                  <ArrowsClockwise size={12} className="mr-1" />{t('refreshSnapshot')}
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-100">
            {[
              [t('totalExpectedIncome'), financialSnapshot.total_expected],
              [t('totalCollected'), financialSnapshot.total_collected],
              [t('totalOutstanding'), financialSnapshot.total_outstanding],
              [t('totalExpenses'), financialSnapshot.total_expenses],
              [t('reserveFund'), financialSnapshot.reserve_fund_balance],
              [t('cashBalance'), financialSnapshot.cash_balance],
              [t('bankBalance'), financialSnapshot.bank_balance],
              [t('repairsTotal'), financialSnapshot.repairs_total],
            ].map(([label, val], idx) => (
              <div key={idx} className="bg-white p-4">
                <p className="text-[10px] text-zinc-400 uppercase tracking-wide">{label}</p>
                <p className="text-base font-bold font-mono mt-1">&euro;{(val || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. Attachments (only for existing meetings) */}
      {editingMeeting && (
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('attachments')}</h3>
            <label className="cursor-pointer">
              <input type="file" className="hidden" onChange={handleFileUpload} data-testid="file-upload-input" />
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-zinc-200 hover:bg-zinc-50 text-zinc-600">
                <UploadSimple size={14} />{uploading ? '...' : t('uploadFile')}
              </span>
            </label>
          </div>
          <div className="divide-y divide-zinc-100">
            {(editingMeeting.attachments || []).length === 0 ? (
              <div className="p-6 text-center text-zinc-400 text-sm">{t('noAttachments') || 'No attachments'}</div>
            ) : (
              editingMeeting.attachments.map(att => (
                <div key={att.file_id} className="px-5 py-2.5 flex items-center justify-between text-sm" data-testid={`attachment-${att.file_id}`}>
                  <a href={`${API}/buildings/${buildingId}/meetings/${editingMeeting.meeting_id}/attachments/${att.file_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900">
                    <Paperclip size={14} />{att.filename}
                  </a>
                  {isAdmin && (
                    <button onClick={() => deleteAttachment(att.file_id)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-red-600" data-testid={`delete-attachment-${att.file_id}`}>
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Phase 197 — Dedicated "PDF Options" panel near the Save action.
          Makes the Financial-Summary opt-in highly visible with a clear status
          indicator showing exactly what will/won't end up in the PDF. */}
      <div
        className={`border-2 ${form.include_financial_summary ? 'border-emerald-300 bg-emerald-50/40' : 'border-zinc-300 bg-zinc-50/40'} px-5 py-4`}
        data-testid="pdf-options-panel"
      >
        <div className="flex items-start gap-3">
          <FilePdf size={22} weight="fill" className={form.include_financial_summary ? 'text-emerald-600 mt-0.5' : 'text-zinc-500 mt-0.5'} />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Επιλογές PDF</p>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold mt-1" data-testid="meeting-include-financial-label">
              <input
                type="checkbox"
                checked={!!form.include_financial_summary}
                onChange={(e) => setForm({ ...form, include_financial_summary: e.target.checked })}
                className="accent-zinc-900 w-4 h-4"
                data-testid="meeting-include-financial-checkbox"
              />
              <span className="text-zinc-900">Εμφάνιση Οικονομικής Κατάστασης στο PDF</span>
            </label>
            <div className="mt-1.5">
              {form.include_financial_summary ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="pdf-fs-status-on">
                  <CheckCircle size={12} weight="fill" /> Θα συμπεριληφθεί στο PDF
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-500" data-testid="pdf-fs-status-off">
                  <X size={12} weight="bold" /> Δεν θα εμφανιστεί στο PDF
                </span>
              )}
              <p className="text-[10px] text-zinc-400 mt-1 italic">
                Επηρεάζει μόνο το PDF — η εμφάνιση μέσα στο σύστημα παραμένει ίδια.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-3 flex-wrap">
        <Button type="button" variant="outline" onClick={() => setView('list')} className="rounded-none" data-testid="cancel-meeting">{t('cancel')}</Button>
        <Button onClick={() => handleSave()} className="bg-emerald-600 hover:bg-emerald-700 rounded-none" data-testid="save-meeting-btn">
          <CheckCircle size={16} className="mr-1.5" />{editingMeeting ? t('save') : t('save')}
        </Button>
        {/* Phase 942 — Direct "Send for Approval" from the form.
            Available when editing an existing DRAFT meeting as admin.
            Chains: save current form → open approval email composer. */}
        {isAdmin && editingMeeting && (editingMeeting.status || 'draft') === 'draft' && (
          <Button
            type="button"
            onClick={handleSendForApprovalFromForm}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-none"
            data-testid="send-for-approval-from-form-btn"
            title="Αποθήκευση + Αποστολή για έγκριση"
          >
            <EnvelopeSimple size={16} className="mr-1.5" />
            {t('sendForApproval') || 'Αποστολή για Έγκριση'}
          </Button>
        )}
      </div>
      <MeetingTemplatesDialog open={templatesDialogOpen} onOpenChange={setTemplatesDialogOpen} />
    </div>
  );
}

// Repair detail sub-component with expand/collapse
function RepairDetail({ repair, t }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mb-2 border border-zinc-100">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-zinc-50 text-left">
        <div className="flex items-center gap-3">
          {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
          <span className="font-medium">{repair.description || '-'}</span>
          <span className={`text-[10px] px-1.5 py-0.5 border ${repair.status === 'active' ? 'bg-zinc-100 text-zinc-700 border-zinc-200' : 'bg-zinc-50 text-zinc-400 border-zinc-200'}`}>{repair.status}</span>
        </div>
        <span className="font-mono font-semibold">&euro;{(repair.amount || 0).toFixed(2)}</span>
      </button>
      {expanded && (repair.apartment_distribution || []).length > 0 && (
        <table className="w-full text-xs border-t border-zinc-100">
          <thead><tr className="bg-zinc-50">
            <th className="px-3 py-1.5 text-left font-medium text-zinc-500">{t('apartmentNumber')}</th>
            <th className="px-3 py-1.5 text-left font-medium text-zinc-500">{t('ownerName')}</th>
            <th className="px-3 py-1.5 text-center font-medium text-zinc-500">{t('sharePercentage')}</th>
            <th className="px-3 py-1.5 text-right font-medium text-zinc-500">{t('allocatedAmount')}</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-100">
            {repair.apartment_distribution.map((d, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 font-semibold">{d.apartment_number}</td>
                <td className="px-3 py-1.5 text-zinc-500">{d.owner_name || '-'}</td>
                <td className="px-3 py-1.5 text-center font-mono">{(d.share_percentage || 0).toFixed(2)}%</td>
                <td className="px-3 py-1.5 text-right font-mono font-semibold">&euro;{(d.allocated_amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
